import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const C = {
  laranja: '#D85A30',
  card: '#ffffff',
  borda: '#e4e9f0',
  texto: '#1a2233',
  textoSub: '#6b7a8d',
  verde: '#1db87a',
  vermelho: '#e74c3c',
  azul: '#2563eb',
  amarelo: '#f59e0b',
  roxo: '#7c3aed',
  bg: '#f7f8fa',
  fundoSub: '#f5f7fa',
};

const STATUS = {
  pendente:          { label: 'Pendente',          bg: '#fff3cd', color: '#856404' },
  aprovado:          { label: 'Aprovado',          bg: '#d1f5e0', color: '#155724' },
  a_cobrar:          { label: 'A Cobrar',          bg: '#dbeafe', color: '#1e40af' },
  cobranca_enviada:  { label: 'Cobrança Enviada',  bg: '#e0f2fe', color: '#0369a1' },
  concluido:         { label: 'Concluído',         bg: '#f3e8ff', color: '#6b21a8' },
  cancelado:         { label: 'Cancelado',         bg: '#fde8e8', color: '#991b1b' },
};

const fmt = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = d => d ? new Date(d).toLocaleDateString('pt-BR') : '';

// ── Helpers de desconto por item (serviço ou peça) ───────────────────────────
// desconto_tipo: 'valor' (R$) ou 'percentual' (%) — desconto_valor: número digitado pelo usuário
// Aplica-se sobre o valor bruto do item (preco, ou preco_unit * qtd)

// Valor bruto do item antes do desconto
const brutoServico = sv => Number(sv.preco || 0);
const brutoPeca = p => Number(p.preco_unit || 0) * Number(p.qtd || 1);

// Calcula o desconto em R$ a partir do bruto + tipo + valor digitado
const calcDescontoReais = (bruto, desconto_tipo, desconto_valor) => {
  const dv = Number(desconto_valor || 0);
  if (dv <= 0) return 0;
  if (desconto_tipo === 'percentual') {
    const d = bruto * (dv / 100);
    return d > bruto ? bruto : d; // nunca desconta mais que o valor do item
  }
  // tipo 'valor' (R$)
  return dv > bruto ? bruto : dv;
};

// Valor líquido (já com desconto aplicado) de um serviço
const liquidoServico = sv => {
  const bruto = brutoServico(sv);
  const desconto = calcDescontoReais(bruto, sv.desconto_tipo, sv.desconto_valor);
  return bruto - desconto;
};

// Valor líquido (já com desconto aplicado) de uma peça
const liquidoPeca = p => {
  const bruto = brutoPeca(p);
  const desconto = calcDescontoReais(bruto, p.desconto_tipo, p.desconto_valor);
  return bruto - desconto;
};

// Totais de uma lista de serviços/peças já considerando desconto
const totalServicosLiquido = (servicos = []) => servicos.reduce((s, sv) => s + liquidoServico(sv), 0);
const totalPecasLiquido = (pecas = []) => pecas.reduce((s, p) => s + liquidoPeca(p), 0);

// Converte serviços/peças "brutos" do orçamento (preco_unit, qtd, desconto_*)
// para o formato de item fiscal esperado pela Focus NFe (valor_unitario, quantidade, valor_total).
// Usado tanto no modal de NF quanto no reenvio direto da listagem.
// codigoServicoPadrao: código LC 116 das Configurações — usado só quando o serviço
// não tem código próprio salvo (o código já usado numa emissão anterior tem prioridade).
const montarItensFiscaisDeOrc = (orc, codigoServicoPadrao = '') => {
  const svs = (orc.servicos || []).filter(s => s.descricao).map(s => {
    const valorLiquido = liquidoServico(s);
    return {
      descricao: s.descricao,
      quantidade: 1,
      valor_unitario: valorLiquido,
      valor_total: valorLiquido,
      tipo: 'servico',
      codigo_servico: (s.codigo_servico || '').trim() || codigoServicoPadrao || '',
      unidade: s.unidade || 'UN',
    };
  });
  const pcs = (orc.pecas || []).filter(p => p.descricao).map(p => {
    const valorLiquidoTotal = liquidoPeca(p);
    const qtd = parseFloat(p.qtd) || 1;
    return {
      descricao: p.descricao,
      quantidade: qtd,
      valor_unitario: valorLiquidoTotal / qtd, // valor unitário já rateando o desconto
      valor_total: valorLiquidoTotal,
      tipo: 'produto',
      ncm: p.ncm || '',
      cfop: p.cfop || '5102',
      unidade: p.unidade || 'UN',
      codigo_servico: '',
    };
  });
  return { svs, pcs, todosItens: [...svs, ...pcs] };
};

// Aplica nos serviços do orçamento o código LC 116 e a unidade dos itens fiscais.
// Os itens fiscais seguem a mesma ordem dos serviços com descrição (ver montarItensFiscaisDeOrc).
const aplicarItensFiscaisNosServicos = (servicos = [], itensFiscais = []) => {
  const svsFiscais = itensFiscais.filter(i => i.tipo === 'servico');
  let idx = 0;
  return servicos.map(sv => {
    if (!sv.descricao) return sv;
    const fiscal = svsFiscais[idx++];
    return fiscal ? { ...sv, codigo_servico: (fiscal.codigo_servico || '').trim(), unidade: fiscal.unidade } : sv;
  });
};

const formatarCPF = (v) => v.replace(/\D/g,'')
  .replace(/(\d{3})(\d)/,'$1.$2')
  .replace(/(\d{3})(\d)/,'$1.$2')
  .replace(/(\d{3})(\d{1,2})$/,'$1-$2')
  .slice(0,14);

const formatarCNPJ = (v) => v.replace(/\D/g,'')
  .replace(/(\d{2})(\d)/,'$1.$2')
  .replace(/(\d{3})(\d)/,'$1.$2')
  .replace(/(\d{3})(\d)/,'$1/$2')
  .replace(/(\d{4})(\d{1,2})$/,'$1-$2')
  .slice(0,18);

const formatarCEP = (v) => {
  let s = v.replace(/\D/g,'');
  if(s.length > 5) s = s.slice(0,5)+'-'+s.slice(5);
  return s.slice(0,9);
};

function Badge({ status }) {
  const s = STATUS[status] || STATUS.pendente;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

function Card({ children, style, ...props }) {
  return (
    <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.borda}`, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: 24, ...style }} {...props}>
      {children}
    </div>
  );
}

// ── Campo de desconto reutilizável (toggle R$ / %) ───────────────────────────
// item: { desconto_tipo: 'valor'|'percentual', desconto_valor: number }
// onChange(campo, valor) — mesma assinatura de setServico/setPeca
function CampoDesconto({ item, onChange, inputStyle }) {
  const tipo = item.desconto_tipo || 'valor';
  return (
    <div style={{ position: 'relative', display: 'flex' }}>
      <input
        type="number"
        step="0.01"
        min="0"
        value={item.desconto_valor || ''}
        onChange={e => onChange('desconto_valor', e.target.value)}
        placeholder="0"
        style={{ ...inputStyle, paddingLeft: 10, paddingRight: 44, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
      />
      <button
        type="button"
        onClick={() => onChange('desconto_tipo', tipo === 'valor' ? 'percentual' : 'valor')}
        title={tipo === 'valor' ? 'Desconto em Reais — clique para mudar para %' : 'Desconto em % — clique para mudar para R$'}
        style={{
          width: 36, flexShrink: 0, border: `1.5px solid ${C.borda}`, borderLeft: 'none',
          borderTopRightRadius: 8, borderBottomRightRadius: 8,
          background: '#f0f3f7', color: C.textoSub, fontWeight: 700, fontSize: 12,
          cursor: 'pointer',
        }}
      >
        {tipo === 'valor' ? 'R$' : '%'}
      </button>
    </div>
  );
}

function Modal({ show, onClose, title, children, maxWidth = 700, bloqueado = false }) {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget && !bloqueado) onClose(); }}>
      <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: C.texto }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.textoSub }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box', background: '#fafbfc' };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4, textTransform: 'uppercase' };

const FORM_VAZIO = {
  numero: '', status: 'pendente',
  cliente_nome: '', cliente_apelido: '', cliente_endereco: '', cliente_bairro: '', cliente_cidade: 'São Paulo-SP',
  cliente_cep: '', cliente_email: '', cliente_telefone: '', cliente_telefone2: '',
  tipo_pessoa: 'juridica', cliente_cnpj: '', cliente_cpf: '', cliente_ie: '', cliente_codigo_municipio: '',
  servicos: [{ descricao: '', preco: '', categoria: '' }],
  pecas: [],
  condicao_pagamento: 'À vista', meio_pagamento: 'PIX\n19.214.891/0001-07',
  garantia: '3 meses', observacoes: '',
};

export default function Orcamento({ toast }) {
  const [orcamentos, setOrcamentos] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetalhe, setShowDetalhe] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [clientesSugeridos, setClientesSugeridos] = useState([]);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const [empresa, setEmpresa] = useState({ nome: "", cnpj: "", endereco: "", telefone: "", cor: "#D85A30", logo: "" });
  const [usuarioLogado] = useState(() => { try { return JSON.parse(localStorage.getItem("usuario") || "{}"); } catch { return {}; } });
  const [modalNota, setModalNota] = useState(null);
const [notasDoOrc, setNotasDoOrc] = useState([]);
const [tipoNota, setTipoNota] = useState('nfse');
const [nfGerarBoleto, setNfGerarBoleto] = useState(false);
const [nfGerandoBoleto, setNfGerandoBoleto] = useState(false);
const [nfAba, setNfAba] = useState('vendedor');
const [nfVendedor, setNfVendedor] = useState('');
const [nfComprador, setNfComprador] = useState('');
const [nfObs, setNfObs] = useState('');
const [nfAnexos, setNfAnexos] = useState([]);
const [nfUsuarios, setNfUsuarios] = useState([]);
const [nfFormaPagamento, setNfFormaPagamento] = useState('boleto');
const [nfCondicao, setNfCondicao] = useState('a_vista');
const [nfParcelas, setNfParcelas] = useState('2');
const [nfDataVenc, setNfDataVenc] = useState('');
const [itensFiscais, setItensFiscais] = useState([]);
const [emitindoNota, setEmitindoNota] = useState(false);
const [salvandoItens, setSalvandoItens] = useState(false);
// Código de serviço padrão (LC 116) da NFS-e — vem de Configurações > NFe — Emissão Fiscal
const [codigoServicoPadrao, setCodigoServicoPadrao] = useState('');

  // CNPJ lookup
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);
  const [cnpjMsg, setCnpjMsg] = useState('');

  // Estoque modal
  const [showEstoque, setShowEstoque] = useState(false);
  const [estoqueItens, setEstoqueItens] = useState([]);
  const [buscaEstoque, setBuscaEstoque] = useState('');

  // Catálogo modal
  const [showCatalogo, setShowCatalogo] = useState(false);
  const [catalogoItens, setCatalogoItens] = useState([]);
  const [buscaCatalogo, setBuscaCatalogo] = useState('');

  // Boleto/PIX modal
  const [showBoleto, setShowBoleto] = useState(false);
  const [dropdownReenviarId, setDropdownReenviarId] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const ITENS_POR_PAGINA = 10;
  const [boletoOrc, setBoletoOrc] = useState(null);
  const [boletoVencDias, setBoletoVencDias] = useState('7'); // legado — mantido para compatibilidade
  const [boletoDataVenc, setBoletoDataVenc] = useState('');
  const [boletoCondicao, setBoletoCondicao] = useState('a_vista');
  const [boletoParcelas, setBoletoParcelas] = useState('2');
  const [boletoGerado, setBoletoGerado] = useState(null); // resultado da API
  const [gerandoBoleto, setGerandoBoleto] = useState(false);
  const [reenviandoCobranca, setReenviandoCobranca] = useState(false);

  // Email modal
  const [showEmail, setShowEmail] = useState(false);
  const [emailOrc, setEmailOrc] = useState(null);
  const [emailDestinatarios, setEmailDestinatarios] = useState([]); // array de { email, selecionado }
  const [emailAssunto, setEmailAssunto] = useState('');
  const [emailCorpo, setEmailCorpo] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [smtpConfigurado, setSmtpConfigurado] = useState(false);
  const [logEnvios, setLogEnvios] = useState([]);
  const [carregandoLog, setCarregandoLog] = useState(false);
  const [showResumo, setShowResumo] = useState(false);
  const [resumoMes, setResumoMes] = useState(''); // '' = todos os meses
  const [resumoAno, setResumoAno] = useState(new Date().getFullYear());
  const [emailTemplate, setEmailTemplate] = useState({ assunto: '', corpo: '' });

  // Rascunho localStorage
  const RASCUNHO_KEY = 'orc_rascunho';

  const carregar = useCallback(async () => {
    try {
      const res = await api.get('/orcamentos');
      setOrcamentos(res.data);
    } catch {}
  }, []);

  // Verifica SMTP e carrega template de email
  const carregarEmailConfig = useCallback(async () => {
    try {
      const res = await api.get('/configuracoes');
      const c = {};
      (res.data || []).forEach(r => { c[r.chave] = r.valor; });
      setSmtpConfigurado(!!(c.smtp_host && c.smtp_usuario && c.smtp_senha));
      setCodigoServicoPadrao((c.nfse_codigo_servico_padrao || '').trim());
      setEmailTemplate({
        assunto: c.email_orc_assunto || 'Orçamento Nº {{numero}} — {{cliente}}',
        corpo: c.email_orc_corpo || 'Olá, {{cliente}}!\n\nSegue em anexo o orçamento Nº {{numero}} no valor de {{valor}}.\n\nQualquer dúvida, estamos à disposição.\n\nAtenciosamente.',
      });
    } catch {}
  }, []);

  useEffect(() => { carregar(); carregarEmailConfig(); }, [carregar, carregarEmailConfig]);

  useEffect(() => {
    api.get("/configuracoes").then(r => {
      const c = {};
      r.data.forEach(x => { c[x.chave] = x.valor; });
      setEmpresa({
        nome:       c.empresa_nome        || "Sobraa",
        cnpj:       c.empresa_cnpj        || "",
        endereco:   c.empresa_endereco    || "",
        telefone:   c.empresa_telefone    || "",
        cor:        c.empresa_cor         || "#D85A30",
        logo:       c.empresa_logo        || "/logo.png",
        logoAltura: Number(c.empresa_logo_altura) || 70,
      });
    }).catch(() => {});
  }, []);

  const novoOrcamento = async () => {
    // Sempre começa limpo — não restaura rascunho ao clicar em Novo Orçamento
    limparRascunho();
    try {
      const res = await api.get('/orcamentos/util/proximo-numero');
      setForm({ ...FORM_VAZIO, numero: res.data.numero });
    } catch {
      const ano = new Date().getFullYear();
      setForm({ ...FORM_VAZIO, numero: `1-${ano}` });
    }
    setBuscaCliente('');
    setClientesSugeridos([]);
    setMostrarSugestoes(false);
    setCnpjMsg('');
    setEditando(null);
    setShowForm(true);
  };

  // Salva rascunho no localStorage sempre que form muda
  useEffect(() => {
    if (showForm) {
      localStorage.setItem(RASCUNHO_KEY, JSON.stringify({ ...form, _editando: !!editando }));
    }
  }, [form, showForm, editando]);

  // Limpa rascunho ao salvar com sucesso
  const limparRascunho = () => localStorage.removeItem(RASCUNHO_KEY);

  // Busca CNPJ — usa rota própria do backend (Focus NFe com fallback BrasilAPI)
  const buscarCNPJ = async (cnpjFormatado) => {
    const limpo = cnpjFormatado.replace(/\D/g, '');
    if (limpo.length !== 14) return;
    setBuscandoCNPJ(true);
    setCnpjMsg('Buscando...');
    try {
      const res = await api.get(`/orcamentos/clientes/consultar-cnpj/${limpo}`);
      if (!res.data) { setCnpjMsg('CNPJ não encontrado.'); setBuscandoCNPJ(false); return; }
      const d = res.data;
      setForm(p => ({
        ...p,
        cliente_nome: d.razao_social || p.cliente_nome,
        cliente_endereco: [d.logradouro, d.numero].filter(Boolean).join(', ') || p.cliente_endereco,
        cliente_bairro: d.bairro || p.cliente_bairro,
        cliente_cidade: d.municipio ? `${d.municipio}${d.uf ? '-' + d.uf : ''}` : p.cliente_cidade,
        cliente_cep: (d.cep || '').replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2') || p.cliente_cep,
        cliente_email: d.email || p.cliente_email,
        cliente_telefone: d.telefone || p.cliente_telefone,
      }));
      setBuscaCliente(d.razao_social || '');
      setCnpjMsg('✅ Dados preenchidos automaticamente!');
    } catch { setCnpjMsg('Erro ao buscar CNPJ.'); }
    setBuscandoCNPJ(false);
  };

  // Carregar itens do catálogo
  const abrirCatalogo = async () => {
    try {
      const res = await api.get('/catalogo');
      setCatalogoItens(res.data);
    } catch {}
    setBuscaCatalogo('');
    setShowCatalogo(true);
  };

  // Adicionar item do catálogo como peça no orçamento
  const adicionarDoCatalogo = (item) => {
    const vp = parseFloat(item.valor_pago || 0);
    const pl = parseFloat(item.percentual_lucro || 0);
    const vv = parseFloat(item.valor_venda || 0);
    const preco = vv > 0 ? vv : vp * (1 + pl / 100);
    const nova = {
      descricao: item.nome,
      marca: item.fornecedor || '',
      unidade: 'un.',
      preco_unit: preco.toFixed(2),
      qtd: 1,
      ncm: item.ncm || '',
      cfop: item.cfop || '5102',
    };
    setForm(p => ({ ...p, pecas: [...p.pecas, nova] }));
    setShowCatalogo(false);
    toast('Item adicionado às peças!', 'success');
  };

  // Carregar itens do estoque
  const abrirEstoque = async () => {
    try {
      const res = await api.get('/estoque');
      setEstoqueItens(res.data.filter(p => parseFloat(p.quantidade) > 0));
    } catch {}
    setBuscaEstoque('');
    setShowEstoque(true);
  };

  // Adicionar item do estoque como peça no orçamento
  const adicionarDoEstoque = (item) => {
    const vp = parseFloat(item.valor_pago || 0);
    const pl = parseFloat(item.percentual_lucro || 0);
    const vf = parseFloat(item.valor_final || 0);
    // Se valor_final for válido usa ele, senão calcula a partir do custo + lucro
    const preco = vf > 0 ? vf : vp * (1 + pl / 100);
    const nova = {
      descricao: item.nome,
      marca: item.fornecedor || '',
      unidade: 'un.',
      preco_unit: preco.toFixed(2),
      qtd: 1,
      ncm: item.ncm || '',
      cfop: item.cfop || '5102',
    };
    setForm(p => ({ ...p, pecas: [...p.pecas, nova] }));
    setShowEstoque(false);
    toast('Item adicionado às peças!', 'success');
  };

  // Abre modal de boleto
  const abrirBoleto = (orc) => {
    setBoletoOrc(orc);
    setBoletoDataVenc('');
    setBoletoCondicao('a_vista');
    setBoletoParcelas('2');
    setBoletoGerado(null);
    setShowBoleto(true);
  };

  // Gera boleto(s) via API — suporta parcelamento
  const gerarBoleto = async () => {
    if (!boletoDataVenc) {
      toast('Informe a data de vencimento.', 'error');
      return;
    }
    setGerandoBoleto(true);
    try {
      const numParcelas = boletoCondicao === 'parcelado' ? parseInt(boletoParcelas) || 1 : 1;
      const totSvs = totalServicosLiquido(boletoOrc.servicos || []);
      const totPcs = totalPecasLiquido(boletoOrc.pecas || []);
      const valorTotal = totSvs + totPcs;
      const valorParcela = numParcelas > 1 ? valorTotal / numParcelas : undefined;

      const [anoV, mesV, diaV] = boletoDataVenc.split('-').map(Number);
      const respostas = [];
      for (let i = 0; i < numParcelas; i++) {
        const dataBase = new Date(anoV, mesV - 1, diaV);
        dataBase.setDate(dataBase.getDate() + i * 30);
        const pad = n => String(n).padStart(2, '0');
        const dataStr = `${dataBase.getFullYear()}-${pad(dataBase.getMonth() + 1)}-${pad(dataBase.getDate())}`;
        const res = await api.post('/cobranca/gerar', {
          orcamento_id: boletoOrc.id,
          data_vencimento: dataStr,
          ...(valorParcela ? { valor: valorParcela } : {}),
          ...(numParcelas > 1 ? { descricao: `Parcela ${i + 1}/${numParcelas}` } : {}),
        });
        respostas.push(res.data);
      }
      // Antes só guardava a última resposta do loop (ultimaResposta) — num
      // orçamento parcelado isso escondia as parcelas anteriores da tela de
      // confirmação, mesmo elas tendo sido geradas certinho no ASAAS.
      setBoletoGerado({ parcelas: respostas });
      carregar();
      toast(numParcelas > 1 ? `${numParcelas} boletos gerados!` : 'Cobrança gerada com sucesso!', 'success');
    } catch (err) {
      const msg = err?.response?.data?.erro || 'Erro ao gerar cobrança.';
      toast(msg, 'error');
    }
    setGerandoBoleto(false);
  };

  const reenviarCobranca = async () => {
    setReenviandoCobranca(true);
    try {
      const emailResp = await api.post('/email-orcamento/enviar-nota', {
        orcamento_id: boletoOrc.id,
      });
      const destinos = emailResp.data?.enviado_para?.join(', ') || boletoOrc.cliente_email;
      toast(`Cobrança reenviada para ${destinos} ✉️`, 'success');
    } catch (err) {
      const msg = err?.response?.data?.erro || 'Erro ao reenviar cobrança.';
      toast(msg, 'error');
    }
    setReenviandoCobranca(false);
  };

  const copiar = (texto, label) => {
    navigator.clipboard.writeText(texto).then(() => toast(`${label} copiado!`, 'success'));
  };

  // Aplica variáveis do template
  const aplicarVars = (texto, orc) => {
    const total = totalServicosLiquido(orc.servicos || []) + totalPecasLiquido(orc.pecas || []);
    return texto
      .replace(/\{\{numero\}\}/g, orc.numero || '')
      .replace(/\{\{cliente\}\}/g, orc.cliente_nome || '')
      .replace(/\{\{valor\}\}/g, fmt(total))
      .replace(/\{\{vencimento\}\}/g, orc.asaas_cobranca_id ? '' : '')
      .replace(/\{\{link_boleto\}\}/g, orc.asaas_cobranca_url || '')
      .replace(/\{\{descricao_cobranca\}\}/g, orc.observacoes || `Orçamento Nº ${orc.numero}`);
  };

  const abrirEmail = async (orc) => {
    setEmailOrc(orc);
    setEmailAssunto(aplicarVars(emailTemplate.assunto, orc));
    setEmailCorpo(aplicarVars(emailTemplate.corpo, orc));
    setLogEnvios([]);
    setShowEmail(true);

    // Busca o cliente cadastrado para pegar emails_adicionais atualizados
    const principal = orc.cliente_email ? [orc.cliente_email] : [];
    let adicionais = [];
    try {
      if (orc.cliente_nome) {
        const res = await api.get(`/orcamentos/clientes/buscar?q=${encodeURIComponent(orc.cliente_nome)}`);
        const clienteEncontrado = (res.data || []).find(c => c.nome === orc.cliente_nome);
        if (clienteEncontrado) {
          const raw = JSON.parse(clienteEncontrado.emails_adicionais || '[]');
          adicionais = raw
            .map(e => typeof e === 'string' ? { email: e, orcamentos: true } : e)
            .filter(e => e.email && e.email.includes('@') && e.orcamentos)
            .map(e => e.email);
        }
      }
    } catch {}
    // Garante que email principal do orçamento sempre aparece
    const todos = [...new Set([...principal, ...adicionais])];
    setEmailDestinatarios(todos.map(email => ({ email, selecionado: true })));

    // Carrega histórico de envios
    setCarregandoLog(true);
    try {
      const res = await api.get(`/email-orcamento/log/${orc.id}`);
      setLogEnvios(res.data || []);
    } catch {
      setLogEnvios([]);
    }
    setCarregandoLog(false);
  };

  const enviarEmail = async () => {
    const selecionados = emailDestinatarios.filter(d => d.selecionado).map(d => d.email);
    if (selecionados.length === 0) { toast('Selecione ao menos um destinatário.', 'error'); return; }
    setEnviandoEmail(true);
    try {
      const pdfHtml = gerarHTMLOrcamento(emailOrc);

      await api.post('/email-orcamento/enviar', {
        orcamento_id: emailOrc.id,
        destinatario: selecionados,
        assunto: emailAssunto,
        corpo: emailCorpo,
        pdf_html: pdfHtml,
      });
      toast(`Email enviado para ${selecionados.join(', ')}!`, 'success');
      // Recarrega log para mostrar o envio recém-feito
      try {
        const res = await api.get(`/email-orcamento/log/${emailOrc.id}`);
        setLogEnvios(res.data || []);
      } catch {}
    } catch (err) {
      toast(err?.response?.data?.erro || 'Erro ao enviar email.', 'error');
      // Recarrega log para mostrar o erro registrado
      try {
        const res = await api.get(`/email-orcamento/log/${emailOrc.id}`);
        setLogEnvios(res.data || []);
      } catch {}
    }
    setEnviandoEmail(false);
  };

  const buscarClientes = async (q) => {
    setBuscaCliente(q);
    if (q.length < 2) { setClientesSugeridos([]); setMostrarSugestoes(false); return; }
    try {
      const res = await api.get(`/orcamentos/clientes/buscar?q=${encodeURIComponent(q)}`);
      const unicos = res.data.filter((c, i, arr) => arr.findIndex(x => x.nome === c.nome && x.telefone === c.telefone) === i);
setClientesSugeridos(unicos);
      setMostrarSugestoes(true);
    } catch {}
  };

  const selecionarCliente = (c) => {
    setForm(p => ({
      ...p,
      cliente_nome: c.nome,
      cliente_endereco: c.endereco || '',
      cliente_bairro: c.bairro || '',
      cliente_cidade: c.cidade || 'São Paulo-SP',
      cliente_cep: c.cep || '',
      cliente_email: c.email || '',
      cliente_telefone: c.telefone || '',
      cliente_telefone2: c.telefone2 || '',
      tipo_pessoa: c.tipo_pessoa || 'juridica',
      cliente_cnpj: c.cnpj || '',
      cliente_cpf: c.cpf || '',
      cliente_ie: c.inscricao_estadual || '',
      cliente_codigo_municipio: c.codigo_municipio || '',
    }));
    setBuscaCliente(c.nome);
    setMostrarSugestoes(false);
  };

  const abrirEdicao = (orc) => {
    setForm({
      numero: orc.numero,
      status: orc.status,
      cliente_nome: orc.cliente_nome || '',
        cliente_apelido: orc.cliente_apelido || '',
      cliente_endereco: orc.cliente_endereco || '',
      cliente_bairro: orc.cliente_bairro || '',
      cliente_cidade: orc.cliente_cidade || 'São Paulo-SP',
      cliente_cep: orc.cliente_cep || '',
      cliente_email: orc.cliente_email || '',
      cliente_telefone: orc.cliente_telefone || '',
      cliente_telefone2: orc.cliente_telefone2 || '',
      tipo_pessoa: orc.tipo_pessoa || 'juridica',
      cliente_cnpj: orc.cliente_cnpj || '',
      cliente_cpf: orc.cliente_cpf || '',
      cliente_ie: orc.cliente_ie || '',
      cliente_codigo_municipio: orc.cliente_codigo_municipio || '',
      servicos: orc.servicos?.length ? orc.servicos : [{ descricao: '', preco: '' }],
      pecas: orc.pecas || [],
      condicao_pagamento: orc.condicao_pagamento || '',
      meio_pagamento: orc.meio_pagamento || '',
      garantia: orc.garantia || '',
      observacoes: orc.observacoes || '',
    });
    setEditando(orc);
    setShowForm(true);
  };

  const salvar = async () => {
    if (!form.cliente_nome) { toast('Informe o nome do cliente.', 'error'); return; }
    setSalvando(true);
    try {
      // Salva/atualiza cliente no banco
      await api.post('/orcamentos/clientes', {
        nome: form.cliente_nome,
        endereco: form.cliente_endereco,
        bairro: form.cliente_bairro,
        cidade: form.cliente_cidade,
        cep: form.cliente_cep,
        email: form.cliente_email,
        telefone: form.cliente_telefone,
        telefone2: form.cliente_telefone2,
        tipo_pessoa: form.tipo_pessoa,     
        cpf: form.cliente_cpf,              
        cnpj: form.cliente_cnpj,
        inscricao_estadual: form.cliente_ie || null,
        codigo_municipio: form.cliente_codigo_municipio || null,
      });
      if (editando) {
        await api.patch(`/orcamentos/${editando.id}`, form);
        toast('Orçamento atualizado!', 'success');
      } else {
        await api.post('/orcamentos', form);
        toast('Orçamento criado!', 'success');
      }
      limparRascunho();
      setShowForm(false);
      carregar();
    } catch { toast('Erro ao salvar orçamento.', 'error'); }
    finally { setSalvando(false); }
  };

  const mudarStatus = async (id, status) => {
    try {
      await api.patch(`/orcamentos/${id}`, { status });
      carregar();
      toast('Status atualizado!', 'success');
    } catch { toast('Erro ao atualizar status.', 'error'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este orçamento?')) return;
    try {
      await api.delete(`/orcamentos/${id}`);
      toast('Orçamento excluído!', 'success');
      setShowDetalhe(false);
      carregar();
    } catch { toast('Erro ao excluir.', 'error'); }
  };

const abrirModalNota = async (orc) => {
  // Monta itens fiscais pré-preenchidos com NCM/CFOP do cadastro
  // IMPORTANTE: usa valor líquido (já com desconto aplicado) — é o valor real cobrado do cliente
  const { pcs, todosItens } = montarItensFiscaisDeOrc(orc, codigoServicoPadrao);
  setItensFiscais(todosItens);
  // Detecção automática: tem serviço E produto → nfe (NF-e cobre tudo), só serviço → nfse
  const temProduto = pcs.length > 0;
  setTipoNota(temProduto ? 'nfe' : 'nfse');
  // Estado de pagamento ASAAS
  setNfGerarBoleto(false);
  setNfAba('vendedor');
  setNfObs(orc.observacoes || '');
  setNfAnexos([]);
  setNfComprador(orc.cliente_nome || '');
  setNfVendedor('');
  setNfFormaPagamento('boleto');
  setNfCondicao('a_vista');
  setNfParcelas('2');
  setNfDataVenc('');
  // Carrega usuários do sistema como opções de Vendedor
  try {
    const uRes = await api.get('/usuarios');
    setNfUsuarios(uRes.data || []);
  } catch { setNfUsuarios([]); }
  setModalNota(orc);

  try {
    const r = await api.get(`/notas/orcamento/${orc.id}`);
    setNotasDoOrc(r.data);
  } catch { setNotasDoOrc([]); }
};

const emitirNota = async (gerarBoleto = false, tipoForcar = null, orcOverride = null) => {
  setEmitindoNota(true);
  try {
    // orcOverride: permite reenviar direto da listagem sem abrir o modal
    const orc = orcOverride || modalNota;
    if (!orc) { setEmitindoNota(false); return; }

    // Monta itensFiscais a partir do orçamento se vier da listagem (sem modal aberto)
    // Usa o mesmo helper do modal para converter preco_unit/qtd -> valor_unitario/quantidade
    // com desconto já aplicado (evita nota sair com valores zerados no reenvio)
    // Reenvio: usa o código salvo no serviço (o da emissão original) e, se não houver,
    // o código padrão das Configurações.
    const itensBase = orcOverride
      ? montarItensFiscaisDeOrc(orcOverride, codigoServicoPadrao).todosItens
      : itensFiscais;

    const temServico = itensBase.some(i => i.tipo === 'servico');
    const temProduto = itensBase.some(i => i.tipo === 'produto');
    const tipoAuto = (temServico && temProduto) ? 'ambos' : temProduto ? 'nfe' : 'nfse';
    const tipoEnvio = tipoForcar || tipoAuto;

    // Filtra itens conforme o tipo a emitir
    const itensFiltrados = tipoForcar === 'nfe'
      ? itensBase.filter(i => i.tipo === 'produto')
      : tipoForcar === 'nfse'
        ? itensBase.filter(i => i.tipo === 'servico')
        : itensBase;

    // NFS-e exige código de serviço em todos os serviços
    if (tipoEnvio !== 'nfe') {
      const semCodigo = itensFiltrados.filter(i => i.tipo === 'servico' && !(i.codigo_servico || '').trim());
      if (semCodigo.length > 0) {
        toast(orcOverride
          ? 'Serviço sem Código LC 116. Cadastre o código padrão em Configurações > NFe — Emissão Fiscal ou abra a nota e informe o código.'
          : 'Informe o Código do Serviço LC 116 em todos os serviços (aba Produtos / Serviços).', 'error');
        setEmitindoNota(false);
        return;
      }
    }

    // Emissão pelo modal: grava no orçamento o código/unidade usados, para que um
    // reenvio futuro use o mesmo código desta nota. Só envia "servicos" (não mexe em peças/estoque).
    if (!orcOverride && temServico) {
      const servicosAtualizados = aplicarItensFiscaisNosServicos(orc.servicos || [], itensFiscais);
      if (JSON.stringify(servicosAtualizados) !== JSON.stringify(orc.servicos || [])) {
        try {
          await api.patch(`/orcamentos/${orc.id}`, { servicos: servicosAtualizados });
        } catch (e) {
          // Não bloqueia a emissão — só o reenvio futuro cairia no código padrão
          console.warn('[NFS-e] Não foi possível salvar o código de serviço no orçamento:', e?.message);
        }
      }
    }

    const r = await api.post('/notas/emitir', {
      orcamento_id: orc.id,
      tipo: tipoEnvio,
      itens_fiscais: itensFiltrados,
    });

    // Se emitiu ambas, mostra resumo do que aconteceu
    if (r.data?.ambos) {
      const erros = r.data.erros || [];
      if (erros.length) {
        toast(`Parcialmente emitido. Erros: ${erros.join(' | ')}`, 'error');
      } else {
        toast('NFS-e (serviços) e NF-e (produtos) enviadas para processamento! ✅', 'success');
      }
    } else {
      toast('Nota enviada para processamento!', 'success');
    }

    // Se "Salvar e Enviar": gera boleto ASAAS + envia email com nota e boleto para o cliente
    if (gerarBoleto) {
      // Validação: data de vencimento obrigatória
      if (!nfDataVenc) {
        toast('Informe a data de vencimento para gerar a cobrança ASAAS.', 'error');
        setEmitindoNota(false);
        return;
      }
      setNfGerandoBoleto(true);
      try {
        const numParcelas = nfCondicao === 'parcelado' ? parseInt(nfParcelas) || 1 : 1;
        const valorTotal = totalServicosLiquido(modalNota.servicos || [])
                         + totalPecasLiquido(modalNota.pecas || [])
                         || modalNota.valor_total || 0;
        const valorParcela = numParcelas > 1 ? (valorTotal / numParcelas) : undefined;

        // Gera uma cobrança por parcela (ou só uma se à vista / a prazo)
        const [anoV, mesV, diaV] = nfDataVenc.split('-').map(Number);
        for (let i = 0; i < numParcelas; i++) {
          // Calcula vencimento de cada parcela: 1ª no dia escolhido, demais +30 dias cada
          const dataBase = new Date(anoV, mesV - 1, diaV);
          dataBase.setDate(dataBase.getDate() + i * 30);
          const pad = n => String(n).padStart(2, '0');
          const dataStr = `${dataBase.getFullYear()}-${pad(dataBase.getMonth() + 1)}-${pad(dataBase.getDate())}`;

          await api.post('/cobranca/gerar', {
            orcamento_id: modalNota.id,
            data_vencimento: dataStr,
            ...(valorParcela ? { valor: valorParcela } : {}),
            descricao: numParcelas > 1 ? `Parcela ${i + 1}/${numParcelas}` : undefined,
          });
        }

        toast(numParcelas > 1
          ? `${numParcelas} boletos gerados no ASAAS! ✅`
          : 'Cobrança ASAAS gerada! ✅', 'success');
      } catch (bErr) {
        toast(bErr?.response?.data?.erro || 'Nota emitida, mas erro ao gerar cobrança.', 'error');
      }
      setNfGerandoBoleto(false);

      // Envia email com nota(s) + link do boleto para todos os emails do cliente
      // O backend buscará os emails corretos do cadastro do cliente (flag notas:true)
      try {
        const emailResp = await api.post('/email-orcamento/enviar-nota', {
          orcamento_id: modalNota.id,
        });
        const destinos = emailResp.data?.enviado_para?.join(', ') || 'cliente';
        toast(`Email enviado para ${destinos} ✉️`, 'success');
      } catch (eErr) {
        const statusCode = eErr?.response?.status;
        const msgErro = eErr?.response?.data?.erro || 'Nota emitida, mas erro ao enviar email.';
        if (statusCode === 408) {
          // Timeout do poll — nota ainda processando
          toast(msgErro, 'error');
        } else if (statusCode === 400 && msgErro.includes('email')) {
          toast('Nota emitida! Nenhum email cadastrado para o cliente.', 'error');
        } else {
          toast(msgErro, 'error');
        }
      }
    }

    // Fecha modal da nota e formulário do orçamento, volta pra lista
    setModalNota(null);
    limparRascunho();
    setShowForm(false);
    setEditando(null);
    carregar();

  } catch (err) {
    toast(err?.response?.data?.erro || 'Erro ao emitir nota', 'error');
  }
  setEmitindoNota(false);
};

const consultarNota = async (notaId) => {
  try {
    const r = await api.get(`/notas/${notaId}/consultar`);
    setNotasDoOrc(prev => prev.map(n => n.id === notaId ? { ...n, ...r.data } : n));
    if (r.data.status === 'autorizada') toast('Nota autorizada! ✅', 'success');
    else if (r.data.status === 'rejeitada') toast('Rejeitada: ' + r.data.mensagem_erro, 'error');
  } catch { toast('Erro ao consultar nota', 'error'); }
};

const setItemFiscal = (i, campo, valor) => {
  setItensFiscais(prev => prev.map((it, j) => j === i ? { ...it, [campo]: valor } : it));
};

// Salva no orçamento as correções feitas na aba "Produtos/Serviços" (ex: NCM, CFOP,
// código de serviço) SEM emitir nota — útil quando a nota deu erro de NCM/etc e você
// só quer corrigir o cadastro para reenviar depois.
const salvarAlteracoesItens = async () => {
  if (!modalNota) return;
  setSalvandoItens(true);
  try {
    const pcsFiltrados = itensFiscais.filter(i => i.tipo === 'produto');

    const servicosAtualizados = aplicarItensFiscaisNosServicos(modalNota.servicos || [], itensFiscais);

    let pcIdx = 0;
    const pecasAtualizadas = (modalNota.pecas || []).map(p => {
      if (!p.descricao) return p;
      const fiscal = pcsFiltrados[pcIdx++];
      return fiscal ? { ...p, ncm: fiscal.ncm, cfop: fiscal.cfop, unidade: fiscal.unidade } : p;
    });

    await api.patch(`/orcamentos/${modalNota.id}`, {
      servicos: servicosAtualizados,
      pecas: pecasAtualizadas,
    });

    // Mantém o modal aberto, só atualiza os dados locais
    setModalNota(prev => prev ? { ...prev, servicos: servicosAtualizados, pecas: pecasAtualizadas } : prev);
    carregar();

    toast('Alterações salvas! Você já pode reenviar a nota.', 'success');
  } catch {
    toast('Erro ao salvar alterações.', 'error');
  } finally {
    setSalvandoItens(false);
  }
};

  // ── Helpers do form ──────────────────────────────────────────────────────
  const addServico = () => setForm(p => ({ ...p, servicos: [...p.servicos, { descricao: '', preco: '', categoria: '', desconto_tipo: 'valor', desconto_valor: '' }] }));
  const remServico = i => setForm(p => ({ ...p, servicos: p.servicos.filter((_, j) => j !== i) }));
  const setServico = (i, key, val) => setForm(p => ({ ...p, servicos: p.servicos.map((s, j) => j === i ? { ...s, [key]: val } : s) }));

  const addPeca = () => setForm(p => ({ ...p, pecas: [...p.pecas, { descricao: '', marca: '', unidade: 'un.', preco_unit: '', qtd: 1, desconto_tipo: 'valor', desconto_valor: '' }] }));
  const remPeca = i => setForm(p => ({ ...p, pecas: p.pecas.filter((_, j) => j !== i) }));
  const setPeca = (i, key, val) => setForm(p => ({ ...p, pecas: p.pecas.map((pc, j) => j === i ? { ...pc, [key]: val } : pc) }));

  const totalServicos = totalServicosLiquido(form.servicos);
  const totalPecas = totalPecasLiquido(form.pecas);
  const totalGeral = totalServicos + totalPecas;

  // ── Exportar PDF ─────────────────────────────────────────────────────────
  // Gera o HTML do orçamento sem abrir janela (usado pelo PDF e pelo email)
  const gerarHTMLOrcamento = (orc) => {
    const svs = orc.servicos || [];
    const pcs = orc.pecas || [];
    const totSvs = totalServicosLiquido(svs);
    const totPcs = totalPecasLiquido(pcs);
    const totGeral = totSvs + totPcs;
    const temAlgumDesconto = svs.some(sv => calcDescontoReais(brutoServico(sv), sv.desconto_tipo, sv.desconto_valor) > 0)
                           || pcs.some(p => calcDescontoReais(brutoPeca(p), p.desconto_tipo, p.desconto_valor) > 0);
    const corEmp = empresa.cor || '#D85A30';
    const logoUrl = empresa.logo || '/logo.png';
    const emailResponsavel = usuarioLogado?.email || '';
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Orçamento ${orc.numero}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #222; margin: 0; padding: 16mm 18mm; }
    .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; border-bottom: 2px solid ${corEmp}; padding-bottom: 16px; }
    .logo-img { height: ${empresa.logoAltura || 70}px; max-width: 220px; object-fit: contain; }
    .empresa { font-size: 11px; color: #555; line-height: 1.7; margin-top: 6px; }
    .empresa strong { font-size: 12px; color: #333; }
    .header-right { text-align: center; }
    .numero-box { font-size: 22px; font-weight: 900; color: ${corEmp}; letter-spacing: -0.5px; }
    .data-box { margin-top: 8px; font-size: 12px; color: #666; display: flex; align-items: center; justify-content: center; gap: 5px; }
    .cliente-box { background: #f9f9f9; border-left: 4px solid ${corEmp}; padding: 12px 16px; margin-bottom: 20px; border-radius: 0 8px 8px 0; display: flex; gap: 40px; align-items: flex-start; }
    .cliente-box .cliente-nome { font-size: 14px; font-weight: 700; color: ${corEmp}; margin-bottom: 4px; }
    .cliente-box .cliente-end { font-size: 11px; color: #555; line-height: 1.7; }
    .cliente-box .cliente-contato { font-size: 11px; color: #555; line-height: 1.9; }
    h2 { color: ${corEmp}; font-size: 14px; margin: 16px 0 8px; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    th { background: ${corEmp}; color: white; padding: 7px 10px; text-align: left; font-size: 11px; }
    td { padding: 6px 10px; border-bottom: 1px solid #f0f0f0; font-size: 12px; }
    tr:nth-child(even) td { background: #fafafa; }
    .totais { margin-left: auto; width: 280px; margin-top: 8px; }
    .totais table td { padding: 6px 10px; }
    .totais .total-final td { background: ${corEmp}; color: white; font-weight: 900; font-size: 15px; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 16px; }
    .info-box { background: #f7f8fa; border-radius: 8px; padding: 12px 14px; }
    .info-box strong { font-size: 11px; color: ${corEmp}; text-transform: uppercase; display: block; margin-bottom: 4px; }
    @media print {
      @page { margin: 16mm 18mm; size: A4; }
      html, body { margin: 0 !important; padding: 0 !important; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <img src="${logoUrl}" class="logo-img" alt="${empresa.nome}" />
      <div class="empresa">
        <strong>${empresa.nome}</strong><br>
        ${empresa.cnpj ? `CNPJ: ${empresa.cnpj}<br>` : ''}
        ${empresa.endereco ? `${empresa.endereco}<br>` : ''}
        ${emailResponsavel ? `${emailResponsavel}` : ''}${empresa.telefone ? ` · ${empresa.telefone}` : ''}
      </div>
    </div>
    <div class="header-right">
      <div class="numero-box">Orçamento ${orc.numero}</div>
      <div class="data-box">📅 ${fmtData(orc.criado_em)}</div>
    </div>
  </div>

  <div class="cliente-box">
    <div>
      <div class="cliente-nome">Cliente: ${orc.cliente_nome}</div>
      <div class="cliente-end" style="font-size:11px;color:#555;margin-bottom:2px">
        ${orc.tipo_pessoa === 'fisica' ? `CPF: ${orc.cliente_cpf || ''}` : `CNPJ: ${orc.cliente_cnpj || ''}`}
      </div>
      <div class="cliente-end">
        ${orc.cliente_endereco ? orc.cliente_endereco : ''}${orc.cliente_bairro ? ', ' + orc.cliente_bairro : ''}<br>
        ${orc.cliente_cidade || ''}${orc.cliente_cep ? ' — CEP ' + orc.cliente_cep : ''}
      </div>
    </div>
    <div class="cliente-contato">
      ${orc.cliente_email ? '✉ ' + orc.cliente_email + '<br>' : ''}
      ${orc.cliente_telefone ? '📞 ' + orc.cliente_telefone + '<br>' : ''}
      ${orc.cliente_telefone2 ? '📞 ' + orc.cliente_telefone2 : ''}
    </div>
  </div>

  ${svs.length > 0 ? `
  <h2>Serviços</h2>
  <table>
    <tr><th>Descrição</th>${temAlgumDesconto ? '<th style="text-align:right">Preço</th><th style="text-align:right">Desconto</th>' : ''}<th style="text-align:right">${temAlgumDesconto ? 'Total' : 'Preço'}</th></tr>
    ${svs.map(sv => {
      const bruto = brutoServico(sv);
      const desc = calcDescontoReais(bruto, sv.desconto_tipo, sv.desconto_valor);
      const liquido = bruto - desc;
      const descLabel = desc > 0
        ? (sv.desconto_tipo === 'percentual' ? `${Number(sv.desconto_valor)}% (${fmt(desc)})` : fmt(desc))
        : '—';
      return `<tr><td>${sv.categoria ? `<small style="display:block;font-size:10px;color:${corEmp};font-weight:700;text-transform:uppercase;margin-bottom:2px">${sv.categoria}</small>` : ''}${sv.descricao}</td>${temAlgumDesconto ? `<td style="text-align:right">${fmt(bruto)}</td><td style="text-align:right;color:#c0392b">${descLabel}</td>` : ''}<td style="text-align:right">${fmt(liquido)}</td></tr>`;
    }).join('')}
  </table>` : ''}

  ${pcs.length > 0 ? `
  <h2>Peças</h2>
  <table>
    <tr><th>Descrição</th><th>Unidade</th><th style="text-align:right">Preço Unit.</th><th style="text-align:center">Qtd.</th>${temAlgumDesconto ? '<th style="text-align:right">Desconto</th>' : ''}<th style="text-align:right">Total</th></tr>
    ${pcs.map(p => {
      const bruto = brutoPeca(p);
      const desc = calcDescontoReais(bruto, p.desconto_tipo, p.desconto_valor);
      const liquido = bruto - desc;
      const descLabel = desc > 0
        ? (p.desconto_tipo === 'percentual' ? `${Number(p.desconto_valor)}% (${fmt(desc)})` : fmt(desc))
        : '—';
      return `<tr>
      <td>${p.descricao}${p.marca ? '<br><small style="color:#888">Marca: ' + p.marca + '</small>' : ''}</td>
      <td>${p.unidade || 'un.'}</td>
      <td style="text-align:right">${fmt(p.preco_unit)}</td>
      <td style="text-align:center">${p.qtd}</td>
      ${temAlgumDesconto ? `<td style="text-align:right;color:#c0392b">${descLabel}</td>` : ''}
      <td style="text-align:right">${fmt(liquido)}</td>
    </tr>`;
    }).join('')}
  </table>` : ''}

  <div class="totais">
    <table>
      ${svs.length > 0 ? `<tr><td>Serviços</td><td style="text-align:right">${fmt(totSvs)}</td></tr>` : ''}
      ${pcs.length > 0 ? `<tr><td>Peças</td><td style="text-align:right">${fmt(totPcs)}</td></tr>` : ''}
      ${temAlgumDesconto ? (() => {
        const totBrutoSvs = svs.reduce((s, sv) => s + brutoServico(sv), 0);
        const totBrutoPcs = pcs.reduce((s, p) => s + brutoPeca(p), 0);
        const descontoTotal = (totBrutoSvs + totBrutoPcs) - totGeral;
        return descontoTotal > 0 ? `<tr><td style="color:#c0392b">Desconto total</td><td style="text-align:right;color:#c0392b">– ${fmt(descontoTotal)}</td></tr>` : '';
      })() : ''}
      <tr class="total-final"><td><strong>Total</strong></td><td style="text-align:right"><strong>${fmt(totGeral)}</strong></td></tr>
    </table>
  </div>

  <div class="grid2" style="margin-top:20px">
    <div class="info-box">
      <strong>Pagamento</strong>
      <div style="font-size:11px;margin-bottom:6px"><b>Meios:</b> ${(orc.meio_pagamento || '').replace(/\n/g, ' · ')}</div>
      <div style="font-size:11px"><b>Condições:</b> ${orc.condicao_pagamento || ''}</div>
    </div>
    <div class="info-box">
      <strong>Garantia</strong>
      <div style="font-size:12px">${orc.garantia || ''}</div>
    </div>
  </div>

  ${orc.observacoes ? `<div class="info-box" style="margin-bottom:16px"><strong>Observações</strong><div style="font-size:12px">${orc.observacoes}</div></div>` : ''}

  <div style="margin-top:30px;text-align:center;color:#888;font-size:11px;border-top:1px solid #eee;padding-top:12px">
    São Paulo, ${fmtData(orc.criado_em)} · ${empresa.nome}
  </div>
</body>
</html>`;

    return html;
  };

  // Abre PDF para impressão (botão 📄)
  const exportarPDF = (orc) => {
    const html = gerarHTMLOrcamento(orc);
    const janela = window.open('', '_blank');
    janela.document.write(html);
    janela.document.close();
    janela.focus();
    setTimeout(() => { janela.print(); }, 500);
  };


  // ── Filtros ──────────────────────────────────────────────────────────────
  const orcFiltradosTodos = orcamentos.filter(o => {
    const matchStatus = filtroStatus === 'todos' || o.status === filtroStatus;
    const matchBusca = !busca || o.cliente_nome?.toLowerCase().includes(busca.toLowerCase()) || o.numero?.includes(busca);
    return matchStatus && matchBusca;
  });
  const totalPaginas = Math.ceil(orcFiltradosTodos.length / ITENS_POR_PAGINA);
  const orcFiltrados = orcFiltradosTodos.slice((paginaAtual - 1) * ITENS_POR_PAGINA, paginaAtual * ITENS_POR_PAGINA);

  const contadores = Object.fromEntries(
    Object.keys(STATUS).map(k => [k, orcamentos.filter(o => o.status === k).length])
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px 20px', maxWidth: 1100, margin: '0 auto' }}>
      <style>{`
        @media (max-width: 600px) {
          .orc-header-cols { display: none !important; }
          .orc-data-row {
            grid-template-columns: 1fr auto !important;
            grid-template-rows: auto auto !important;
          }
          .orc-data-row > :nth-child(1) { grid-column: 1; grid-row: 1; }
          .orc-data-row > :nth-child(2) { grid-column: 1; grid-row: 2; text-align: left !important; font-size: 13px; }
          .orc-data-row > :nth-child(3) { grid-column: 2; grid-row: 2; justify-content: flex-end !important; }
          .orc-data-row > :nth-child(4) { grid-column: 2; grid-row: 1; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.texto }}>Orçamentos</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textoSub }}>{orcamentos.length} orçamento(s) no total</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowResumo(true)} style={{ padding: '10px 18px', background: 'white', color: C.texto, border: `1px solid ${C.borda}`, borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            📊 Resumo
          </button>
          <button onClick={novoOrcamento} style={{ padding: '10px 20px', background: C.laranja, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            + Novo Orçamento
          </button>
        </div>
      </div>

      {/* Cards de status */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[{ key: 'todos', label: 'Todos', count: orcamentos.length, cor: C.texto },
          ...Object.entries(STATUS).map(([k, v]) => ({ key: k, label: v.label, count: contadores[k] || 0, cor: v.color }))
        ].map(item => (
          <Card key={item.key} style={{ padding: '14px 18px', cursor: 'pointer', border: filtroStatus === item.key ? `2px solid ${C.laranja}` : `1px solid ${C.borda}` }}
            onClick={() => { setFiltroStatus(item.key); setPaginaAtual(1); }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textoSub, textTransform: 'uppercase' }}>{item.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: item.cor, marginTop: 2 }}>{item.count}</div>
          </Card>
        ))}
      </div>

      {/* Busca */}
      <div style={{ marginBottom: 16 }}>
        <input value={busca} onChange={e => { setBusca(e.target.value); setPaginaAtual(1); }}
          placeholder="Buscar por cliente ou número..."
          style={{ ...inputStyle, padding: '10px 16px', fontSize: 14 }} />
      </div>

      {/* Lista de orçamentos */}
      <Card style={{ padding: 0, overflow: 'visible' }}>
        {orcFiltrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: C.textoSub }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <p>Nenhum orçamento encontrado</p>
          </div>
        ) : (<>
          {/* Cabeçalho de colunas — desktop only */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 36px', gap: 8, padding: '7px 16px', borderBottom: `1px solid ${C.borda}`, alignItems: 'center' }}
               className="orc-header-cols">
            <span style={{ fontSize: 11, fontWeight: 600, color: C.textoSub, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cliente</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: C.textoSub, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right' }}>Valor</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: C.textoSub, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center' }}>Status</span>
            <span />
          </div>

          {orcFiltrados.map((orc, idx) => {
            const totSvs = totalServicosLiquido(orc.servicos || []);
            const totPcs = totalPecasLiquido(orc.pecas || []);
            const total = totSvs + totPcs;
            return (
              <div key={orc.id} style={{ borderBottom: idx < orcFiltrados.length - 1 ? `1px solid ${C.borda}` : 'none' }}>

                {/* Linha de dados — grid com colunas fixas */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 36px', gap: 8, padding: '10px 16px 6px', alignItems: 'center' }}
                     className="orc-data-row">
                  {/* Coluna: Nome + subtítulo */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.texto, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{orc.cliente_nome}</div>
                    {orc.cliente_apelido && <div style={{ fontSize: 11, color: C.laranja, fontWeight: 600 }}>{orc.cliente_apelido}</div>}
                    <div style={{ fontSize: 11, color: C.textoSub, marginTop: 1 }}>Nº {orc.numero} · {fmtData(orc.criado_em)}</div>
                  </div>

                  {/* Coluna: Valor — fixo 120px, sempre alinhado */}
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.laranja, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fmt(total)}
                  </div>

                  {/* Coluna: Badge de status — fixo 140px, centralizado */}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Badge status={orc.status} />
                  </div>

                  {/* Coluna: Botão ⋯ — fixo 36px */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setDropdownReenviarId(p => p === orc.id ? null : orc.id)}
                      title="Mais opções"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: dropdownReenviarId === orc.id ? C.fundoSub : 'transparent', border: `1px solid ${dropdownReenviarId === orc.id ? C.borda : 'transparent'}`, borderRadius: 6, cursor: 'pointer', color: C.textoSub, fontSize: 16 }}>
                      ⋯
                    </button>
                    {dropdownReenviarId === orc.id && (
                      <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 300, marginTop: 4, background: 'white', border: `1px solid ${C.borda}`, borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.13)', minWidth: 220, overflow: 'hidden' }}>

                        {/* Seção: Situação */}
                        <div style={{ padding: '8px 0 4px', borderBottom: `1px solid ${C.borda}` }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: C.textoSub, padding: '0 14px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Situação</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, padding: '0 8px 6px' }}>
                            {Object.entries(STATUS).map(([k, v]) => (
                              <button key={k} onClick={() => { mudarStatus(orc.id, k); setDropdownReenviarId(null); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 6, border: 'none', background: orc.status === k ? C.fundoSub : 'transparent', cursor: 'pointer', fontSize: 11, fontWeight: orc.status === k ? 700 : 500, color: C.texto, textAlign: 'left' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: v.color || C.textoSub, flexShrink: 0 }} />
                                {v.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Seção: Ações principais */}
                        <div style={{ padding: '6px 0', borderBottom: `1px solid ${C.borda}` }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: C.textoSub, padding: '0 14px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ações</div>
                          {[
                            { label: 'Editar orçamento', icon: '✏️', action: () => abrirEdicao(orc) },
                            { label: 'Emitir nota fiscal (NF)', icon: '🧾', action: () => abrirModalNota(orc) },
                            { label: smtpConfigurado ? 'Enviar por email' : 'Email (configure SMTP)', icon: '✉️', action: () => smtpConfigurado ? abrirEmail(orc) : toast('Configure o SMTP em Configurações → Integrações.', 'error'), muted: !smtpConfigurado },
                            { label: orc.asaas_cobranca_id ? 'Ver cobrança ASAAS' : 'Gerar cobrança', icon: '💳', action: () => abrirBoleto(orc) },
                            { label: 'Baixar PDF', icon: '📄', action: () => exportarPDF(orc) },
                          ].map((item, i) => (
                            <button key={i} onClick={() => { item.action(); setDropdownReenviarId(null); }}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: item.muted ? C.textoSub : C.texto, opacity: item.muted ? 0.6 : 1 }}
                              onMouseEnter={e => e.currentTarget.style.background = C.fundoSub}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                              <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>{item.icon}</span>
                              {item.label}
                            </button>
                          ))}
                        </div>

                        {/* Seção: Reenviar NF */}
                        {((orc.servicos || []).length > 0 || (orc.pecas || []).length > 0) && (
                          <div style={{ padding: '6px 0', borderBottom: `1px solid ${C.borda}` }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.textoSub, padding: '0 14px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reenviar nota</div>
                            {(orc.servicos || []).length > 0 && (
                              <button onClick={() => { setDropdownReenviarId(null); emitirNota(false, 'nfse', orc); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: C.texto }}
                                onMouseEnter={e => e.currentTarget.style.background = C.fundoSub}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>🛠</span> Reenviar NFS-e (serviços)
                              </button>
                            )}
                            {((orc.pecas || orc.produtos || []).filter(p => p && (p.descricao || p.nome)).length > 0) && (
                              <button onClick={() => { setDropdownReenviarId(null); emitirNota(false, 'nfe', { ...orc, pecas: orc.pecas || orc.produtos || [] }); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: C.texto }}
                                onMouseEnter={e => e.currentTarget.style.background = C.fundoSub}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>📦</span> Reenviar NF-e (produtos)
                              </button>
                            )}
                          </div>
                        )}

                        {/* Seção: Excluir */}
                        <div style={{ padding: '6px 0' }}>
                          <button onClick={() => { excluir(orc.id); setDropdownReenviarId(null); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: C.vermelho }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>🗑</span> Excluir orçamento
                          </button>
                        </div>

                      </div>
                    )}
                  </div>
                </div>

                {/* Botões de ação rápida */}
                <div style={{ display: 'flex', gap: 6, padding: '0 16px 10px', flexWrap: 'wrap' }}>
                  <button onClick={() => abrirEdicao(orc)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: '#eef2ff', color: C.azul, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    ✏️ Editar
                  </button>
                  <button onClick={() => abrirModalNota(orc)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: '#fff8f0', color: C.laranja, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    🧾 NF
                  </button>
                  <button onClick={() => smtpConfigurado ? abrirEmail(orc) : toast('Configure o SMTP em Configurações → Integrações.', 'error')}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: smtpConfigurado ? '#f0fdf4' : '#f7f8fa', color: smtpConfigurado ? '#15803d' : C.textoSub, border: 'none', borderRadius: 6, cursor: smtpConfigurado ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, opacity: smtpConfigurado ? 1 : 0.6 }}>
                    ✉️ Email
                  </button>
                </div>

              </div>
            );
          })}

          {/* Paginação */}
          {totalPaginas > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 16px', borderTop: `1px solid ${C.borda}` }}>
              <button onClick={() => setPaginaAtual(p => Math.max(1, p - 1))} disabled={paginaAtual === 1}
                style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.borda}`, background: 'white', cursor: paginaAtual === 1 ? 'not-allowed' : 'pointer', fontSize: 13, color: paginaAtual === 1 ? C.textoSub : C.texto, opacity: paginaAtual === 1 ? 0.5 : 1 }}>
                ← Anterior
              </button>
              <span style={{ fontSize: 13, color: C.textoSub }}>
                Página <b style={{ color: C.texto }}>{paginaAtual}</b> de <b style={{ color: C.texto }}>{totalPaginas}</b>
              </span>
              <button onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))} disabled={paginaAtual === totalPaginas}
                style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.borda}`, background: 'white', cursor: paginaAtual === totalPaginas ? 'not-allowed' : 'pointer', fontSize: 13, color: paginaAtual === totalPaginas ? C.textoSub : C.texto, opacity: paginaAtual === totalPaginas ? 0.5 : 1 }}>
                Próxima →
              </button>
            </div>
          )}
        </>)}
      </Card>

      {/* Modal: Formulário de orçamento */}
      <Modal show={showForm} onClose={() => { limparRascunho(); setShowForm(false); }} title={editando ? `Editar Orçamento ${editando.numero}` : 'Novo Orçamento'} maxWidth={820} bloqueado={true}>

        {/* Número (informativo) e status */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div style={{ background: '#f7f8fa', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textoSub, textTransform: 'uppercase', marginBottom: 4 }}>Número</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.laranja }}>{form.numero}</div>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} style={{ ...inputStyle }}>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>

        {/* Dados do cliente */}
        <div style={{ background: '#fff8f0', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.laranja, marginBottom: 12, textTransform: 'uppercase' }}>Dados do Cliente</div>

          {/* Busca de cliente */}
          <div style={{ marginBottom: 12, position: 'relative' }}>
            <label style={labelStyle}>Buscar cliente cadastrado</label>
            <input
  value={buscaCliente}
  onChange={e => buscarClientes(e.target.value)}
  onFocus={() => buscaCliente.length >= 2 && setMostrarSugestoes(true)}
  onBlur={() => setTimeout(() => setMostrarSugestoes(false), 150)}
  placeholder="Digite o nome para buscar..."
  style={{ ...inputStyle, background: 'white' }}
/>
            {mostrarSugestoes && clientesSugeridos.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: `1px solid ${C.borda}`, borderRadius: 8, zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto' }}>
                {clientesSugeridos.map(c => (
                  <div key={c.id} onClick={() => selecionarCliente(c)}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${C.borda}`, fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fff8f0'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <div style={{ fontWeight: 700, color: C.texto }}>{c.nome}</div>
                    {c.telefone && <div style={{ fontSize: 11, color: C.textoSub }}>{c.telefone}{c.email ? ' · ' + c.email : ''}</div>}
                  </div>
                ))}
              </div>
            )}
            {mostrarSugestoes && clientesSugeridos.length === 0 && buscaCliente.length >= 2 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: `1px solid ${C.borda}`, borderRadius: 8, zIndex: 100, padding: '10px 14px', fontSize: 12, color: C.textoSub }}>
                Nenhum cliente encontrado — preencha os dados abaixo para cadastrar.
              </div>
            )}
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Nome *</label>
            <input value={form.cliente_nome} onChange={e => setForm(p => ({ ...p, cliente_nome: e.target.value }))} placeholder="Nome do cliente" style={inputStyle} />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Apelido / Nome Fantasia</label>
            <input value={form.cliente_apelido || ''} onChange={e => setForm(p => ({ ...p, cliente_apelido: e.target.value }))} placeholder="Ex: Checkout Viagens" style={inputStyle} />
          </div>

          {/* Tipo de Pessoa */}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Tipo de Pessoa</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              {[['juridica', '🏢 Pessoa Jurídica'], ['fisica', '👤 Pessoa Física']].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setForm(p => ({ ...p, tipo_pessoa: val }))} style={{
                  padding: '8px', border: `2px solid ${form.tipo_pessoa === val ? C.laranja : C.borda}`,
                  borderRadius: 8, background: form.tipo_pessoa === val ? '#fff8f0' : 'white',
                  cursor: 'pointer', fontWeight: 600, fontSize: 13,
                  color: form.tipo_pessoa === val ? C.laranja : C.textoSub,
                }}>
                  {label}
                </button>
              ))}
            </div>
            {form.tipo_pessoa === 'juridica' ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>CNPJ</label>
                    <input value={form.cliente_cnpj}
                      onChange={e => {
                        const val = formatarCNPJ(e.target.value);
                        setForm(p => ({ ...p, cliente_cnpj: val }));
                        if (val.replace(/\D/g, '').length === 14) buscarCNPJ(val);
                      }}
                      placeholder="CNPJ (00.000.000/0001-00)" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Inscrição Estadual
                      {buscandoCNPJ && <span style={{ marginLeft: 6, color: '#856404' }}>⏳</span>}
                    </label>
                    <input
                      value={form.cliente_ie || ''}
                      onChange={e => setForm(p => ({ ...p, cliente_ie: e.target.value }))}
                      placeholder="Preenchida automaticamente"
                      style={inputStyle}
                    />
                  </div>
                </div>
                {cnpjMsg && (
                  <div style={{ fontSize: 12, marginTop: 4, color: cnpjMsg.startsWith('✅') ? '#155724' : buscandoCNPJ ? '#856404' : C.vermelho }}>
                    {cnpjMsg}
                  </div>
                )}
              </div>
            ) : (
              <input value={form.cliente_cpf} onChange={e => setForm(p => ({ ...p, cliente_cpf: formatarCPF(e.target.value) }))}
  placeholder="CPF (000.000.000-00)" style={inputStyle} />
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={labelStyle}>Endereço</label>
              <input value={form.cliente_endereco} onChange={e => setForm(p => ({ ...p, cliente_endereco: e.target.value }))} placeholder="Rua, número, complemento" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Bairro</label>
              <input value={form.cliente_bairro} onChange={e => setForm(p => ({ ...p, cliente_bairro: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={labelStyle}>Cidade</label>
              <input value={form.cliente_cidade} onChange={e => setForm(p => ({ ...p, cliente_cidade: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>CEP</label>
              <input value={form.cliente_cep} onChange={e => {
  const val = formatarCEP(e.target.value);
  setForm(p => ({ ...p, cliente_cep: val }));
  if (val.replace('-','').length === 8) {
    // Proxy backend (evita chamada direta do browser a API externa — regra de CORS/segurança)
    api.get(`/utils/cep/${val.replace('-','')}`)
      .then(r => r.data)
      .then(d => {
        if (!d.erro) setForm(p => ({
          ...p,
          cliente_endereco: d.logradouro || p.cliente_endereco,
          cliente_bairro: d.bairro || p.cliente_bairro,
          cliente_cidade: d.localidade ? `${d.localidade}-${d.uf}` : p.cliente_cidade,
          // Código IBGE do município — necessário para NFS-e (codigo_municipio_tomador)
          cliente_codigo_municipio: d.ibge || p.cliente_codigo_municipio,
          cliente_cep: val
        }));
      })
      .catch(() => {});
  }
}}
/>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={form.cliente_email} onChange={e => setForm(p => ({ ...p, cliente_email: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Telefone</label>
              <input value={form.cliente_telefone} onChange={e => setForm(p => ({ ...p, cliente_telefone: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Telefone 2</label>
              <input value={form.cliente_telefone2} onChange={e => setForm(p => ({ ...p, cliente_telefone2: e.target.value }))} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Serviços */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.azul, textTransform: 'uppercase' }}>Serviços</div>
            <button onClick={addServico} style={{ padding: '4px 12px', background: C.azul, color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>+ Adicionar</button>
          </div>
          {form.servicos.map((sv, i) => {
            const bruto = brutoServico(sv);
            const descontoReais = calcDescontoReais(bruto, sv.desconto_tipo, sv.desconto_valor);
            const liquido = bruto - descontoReais;
            return (
            <div key={i} style={{ border: `1px solid ${C.borda}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 140px 32px', gap: 8, marginBottom: descontoReais > 0 || bruto > 0 ? 8 : 0 }}>
                <select
                  value={sv.categoria || ''}
                  onChange={e => setServico(i, 'categoria', e.target.value)}
                  style={{ ...inputStyle, fontSize: 12 }}
                >
                  <option value="">Categoria</option>
                  <option value="Serviços de T.I">Serviços de T.I</option>
                  <option value="Serviços de CFTV">Serviços de CFTV</option>
                  <option value="Serviços de Telefonia">Serviços de Telefonia</option>
                </select>
                <textarea
                  value={sv.descricao}
                  onChange={e => setServico(i, 'descricao', e.target.value)}
                  placeholder="Descrição do serviço"
                  style={{ ...inputStyle, minHeight: '64px', resize: 'vertical', lineHeight: '1.5' }}
                />
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: C.textoSub, fontWeight: 600 }}>R$</span>
                  <input type="number" step="0.01" value={sv.preco} onChange={e => setServico(i, 'preco', e.target.value)} placeholder="0,00" style={{ ...inputStyle, paddingLeft: 32 }} />
                </div>
                <button onClick={() => remServico(i)} style={{ background: '#fef2f2', color: C.vermelho, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>✕</button>
              </div>
              {bruto > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '160px 120px 1fr', gap: 8, alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: C.textoSub, textAlign: 'right', paddingRight: 4 }}>Desconto</div>
                  <CampoDesconto item={sv} onChange={(campo, val) => setServico(i, campo, val)} inputStyle={inputStyle} />
                  <div style={{ fontSize: 12, color: C.textoSub, textAlign: 'right' }}>
                    {descontoReais > 0 ? (
                      <>
                        <span style={{ textDecoration: 'line-through', marginRight: 6 }}>{fmt(bruto)}</span>
                        <span style={{ fontWeight: 700, color: C.verde }}>{fmt(liquido)}</span>
                      </>
                    ) : (
                      <span style={{ fontWeight: 700, color: C.texto }}>{fmt(liquido)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* Peças */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.verde, textTransform: 'uppercase' }}>Peças</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={abrirCatalogo} style={{ padding: '4px 12px', background: '#f5f0ff', color: '#7c3aed', border: '1px solid #7c3aed', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>📋 Catálogo</button>
              <button onClick={abrirEstoque} style={{ padding: '4px 12px', background: '#f0fdf4', color: C.verde, border: `1px solid ${C.verde}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>📦 Do Estoque</button>
              <button onClick={addPeca} style={{ padding: '4px 12px', background: C.verde, color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>+ Adicionar</button>
            </div>
          </div>
          {form.pecas.map((p, i) => {
            const bruto = brutoPeca(p);
            const descontoReais = calcDescontoReais(bruto, p.desconto_tipo, p.desconto_valor);
            const liquido = bruto - descontoReais;
            return (
            <div key={i} style={{ background: '#f7fdf9', borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                <input value={p.descricao} onChange={e => setPeca(i, 'descricao', e.target.value)} placeholder="Descrição da peça" style={inputStyle} />
                <input value={p.marca} onChange={e => setPeca(i, 'marca', e.target.value)} placeholder="Marca (opcional)" style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 120px 80px 32px', gap: 8, marginBottom: bruto > 0 ? 6 : 0 }}>
                <input value={p.unidade} onChange={e => setPeca(i, 'unidade', e.target.value)} placeholder="un." style={inputStyle} />
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: C.textoSub, fontWeight: 600 }}>R$</span>
                  <input type="number" step="0.01" value={p.preco_unit} onChange={e => setPeca(i, 'preco_unit', e.target.value)} placeholder="0,00" style={{ ...inputStyle, paddingLeft: 32 }} />
                </div>
                <input type="number" value={p.qtd} onChange={e => setPeca(i, 'qtd', e.target.value)} placeholder="Qtd" style={inputStyle} />
                <button onClick={() => remPeca(i)} style={{ background: '#fef2f2', color: C.vermelho, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>✕</button>
              </div>
              {bruto > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '80px 120px 1fr', gap: 8, alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: C.textoSub, textAlign: 'right', paddingRight: 4 }}>Desc.</div>
                  <CampoDesconto item={p} onChange={(campo, val) => setPeca(i, campo, val)} inputStyle={inputStyle} />
                  <div style={{ fontSize: 12, color: C.textoSub, textAlign: 'right' }}>
                    {descontoReais > 0 ? (
                      <>
                        <span style={{ textDecoration: 'line-through', marginRight: 6 }}>{fmt(bruto)}</span>
                        <span style={{ fontWeight: 700, color: C.verde }}>{fmt(liquido)}</span>
                      </>
                    ) : (
                      <span style={{ fontWeight: 700, color: C.texto }}>{fmt(liquido)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* Total */}
        {(form.servicos.length > 0 || form.pecas.length > 0) && (
          <div style={{ background: '#fff8f0', borderRadius: 8, padding: '10px 16px', marginBottom: 20, display: 'flex', justifyContent: 'flex-end', gap: 24 }}>
            {totalServicos > 0 && <span style={{ fontSize: 13, color: C.textoSub }}>Serviços: <b>{fmt(totalServicos)}</b></span>}
            {totalPecas > 0 && <span style={{ fontSize: 13, color: C.textoSub }}>Peças: <b>{fmt(totalPecas)}</b></span>}
            <span style={{ fontSize: 15, fontWeight: 800, color: C.laranja }}>Total: {fmt(totalGeral)}</span>
          </div>
        )}

        {/* Pagamento e garantia */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>Meios de Pagamento</label>
            <textarea value={form.meio_pagamento} onChange={e => setForm(p => ({ ...p, meio_pagamento: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>Condições de Pagamento</label>
            <textarea value={form.condicao_pagamento} onChange={e => setForm(p => ({ ...p, condicao_pagamento: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>Garantia</label>
            <input value={form.garantia} onChange={e => setForm(p => ({ ...p, garantia: e.target.value }))} style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Observações</label>
          <textarea value={form.observacoes} onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => { limparRascunho(); setShowForm(false); }} style={{ flex: 1, padding: '12px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={salvar} disabled={salvando} style={{ flex: 2, padding: '12px', background: C.laranja, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            {salvando ? 'Salvando...' : editando ? '💾 Atualizar' : '💾 Criar Orçamento'}
          </button>
        </div>
      </Modal>

      {/* Modal: Selecionar do Catálogo */}
      <Modal show={showCatalogo} onClose={() => setShowCatalogo(false)} title="📋 Adicionar do Catálogo" maxWidth={600}>
        <input
          value={buscaCatalogo}
          onChange={e => setBuscaCatalogo(e.target.value)}
          placeholder="Buscar produto no catálogo..."
          style={{ ...inputStyle, marginBottom: 16 }}
          autoFocus
        />
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {catalogoItens.filter(p =>
            p.nome.toLowerCase().includes(buscaCatalogo.toLowerCase()) ||
            (p.categoria || '').toLowerCase().includes(buscaCatalogo.toLowerCase()) ||
            (p.fornecedor || '').toLowerCase().includes(buscaCatalogo.toLowerCase())
          ).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: C.textoSub }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
              <p>Nenhum produto no catálogo</p>
            </div>
          ) : catalogoItens
            .filter(p =>
              p.nome.toLowerCase().includes(buscaCatalogo.toLowerCase()) ||
              (p.categoria || '').toLowerCase().includes(buscaCatalogo.toLowerCase()) ||
              (p.fornecedor || '').toLowerCase().includes(buscaCatalogo.toLowerCase())
            )
            .map(item => (
              <div key={item.id}
                onClick={() => adicionarDoCatalogo(item)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: `1px solid ${C.borda}`, cursor: 'pointer', borderRadius: 8 }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f0ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.texto }}>{item.nome}</div>
                  <div style={{ fontSize: 12, color: C.textoSub, marginTop: 2 }}>
                    {item.categoria && <span style={{ marginRight: 8 }}>{item.categoria}</span>}
                    {item.fornecedor && <span>🏭 {item.fornecedor}</span>}
                  </div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#7c3aed', whiteSpace: 'nowrap', marginLeft: 12 }}>
                  {Number(item.valor_venda || item.valor_pago || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </div>
              </div>
            ))}
        </div>
        <div style={{ marginTop: 16 }}>
          <button onClick={() => setShowCatalogo(false)} style={{ width: '100%', padding: '10px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
            Cancelar
          </button>
        </div>
      </Modal>

      {/* Modal: Selecionar do Estoque */}
      <Modal show={showEstoque} onClose={() => setShowEstoque(false)} title="📦 Adicionar do Estoque" maxWidth={600}>
        <input
          value={buscaEstoque}
          onChange={e => setBuscaEstoque(e.target.value)}
          placeholder="Buscar produto no estoque..."
          style={{ ...inputStyle, marginBottom: 16 }}
          autoFocus
        />
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {estoqueItens.filter(p =>
            p.nome.toLowerCase().includes(buscaEstoque.toLowerCase()) ||
            (p.categoria || '').toLowerCase().includes(buscaEstoque.toLowerCase())
          ).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: C.textoSub }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <p>Nenhum produto disponível em estoque</p>
            </div>
          ) : estoqueItens
            .filter(p =>
              p.nome.toLowerCase().includes(buscaEstoque.toLowerCase()) ||
              (p.categoria || '').toLowerCase().includes(buscaEstoque.toLowerCase())
            )
            .map(item => (
              <div key={item.id}
                onClick={() => adicionarDoEstoque(item)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: `1px solid ${C.borda}`, cursor: 'pointer', borderRadius: 8 }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.texto }}>{item.nome}</div>
                  <div style={{ fontSize: 12, color: C.textoSub, marginTop: 2 }}>
                    {item.categoria && <span style={{ marginRight: 8 }}>{item.categoria}</span>}
                    Qtd: <b>{parseFloat(item.quantidade)}</b>
                    {item.local && <span style={{ marginLeft: 8 }}>📍 {item.local}</span>}
                  </div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 14, color: C.verde, whiteSpace: 'nowrap', marginLeft: 12 }}>
                  {Number(item.valor_final || item.valor_pago || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </div>
              </div>
            ))}
        </div>
        <div style={{ marginTop: 16 }}>
          <button onClick={() => setShowEstoque(false)} style={{ width: '100%', padding: '10px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
            Cancelar
          </button>
        </div>
      </Modal>

      {/* Modal: Boleto/PIX */}
      <Modal show={showBoleto} onClose={() => { setShowBoleto(false); setBoletoGerado(null); }} title="🏦 Gerar Boleto / PIX" maxWidth={520}>
        {boletoOrc && !boletoGerado && (
          <div>
            {/* Resumo do orçamento */}
            <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: C.textoSub, marginBottom: 4 }}>Orçamento Nº {boletoOrc.numero}</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.texto }}>{boletoOrc.cliente_nome}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.laranja, marginTop: 6 }}>
                {(() => {
                  const totSvs = totalServicosLiquido(boletoOrc.servicos || []);
                  const totPcs = totalPecasLiquido(boletoOrc.pecas || []);
                  return fmt(totSvs + totPcs);
                })()}
              </div>
            </div>

            {/* Aviso + reenvio se já tem cobrança */}
            {boletoOrc.asaas_cobranca_id && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#856404', marginBottom: 10 }}>
                  ⚠️ Este orçamento já possui uma cobrança gerada. Gerar novamente criará uma <b>nova cobrança</b> no ASAAS.
                </div>
                <button onClick={reenviarCobranca} disabled={reenviandoCobranca}
                  style={{ width: '100%', padding: '10px', background: '#15803d', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: reenviandoCobranca ? 'not-allowed' : 'pointer', opacity: reenviandoCobranca ? 0.7 : 1 }}>
                  {reenviandoCobranca ? '⏳ Reenviando...' : '✉️ Reenviar Cobrança por Email'}
                </button>
              </div>
            )}

            {/* Condição de pagamento */}
            <div style={{ display: 'grid', gridTemplateColumns: boletoCondicao === 'parcelado' ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Condição</label>
                <select value={boletoCondicao} onChange={e => { setBoletoCondicao(e.target.value); if (e.target.value !== 'parcelado') setBoletoParcelas('2'); }}
                  style={{ ...inputStyle, background: 'white' }}>
                  <option value="a_vista">À Vista</option>
                  <option value="a_prazo">A Prazo</option>
                  <option value="parcelado">Parcelado</option>
                </select>
              </div>
              {boletoCondicao === 'parcelado' && (
                <div>
                  <label style={labelStyle}>Nº de Parcelas</label>
                  <select value={boletoParcelas} onChange={e => setBoletoParcelas(e.target.value)}
                    style={{ ...inputStyle, background: 'white' }}>
                    {Array.from({ length: 11 }, (_, i) => i + 2).map(n => (
                      <option key={n} value={String(n)}>{n}x</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Data de vencimento */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Data de Vencimento *{boletoCondicao === 'parcelado' ? ` (1º boleto — demais a cada 30 dias)` : ''}</label>
              <input
                type="date"
                value={boletoDataVenc}
                onChange={e => setBoletoDataVenc(e.target.value)}
                style={{ ...inputStyle, borderColor: !boletoDataVenc ? '#ef4444' : undefined }}
              />
              {!boletoDataVenc && (
                <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Obrigatória</div>
              )}
              {boletoCondicao === 'parcelado' && boletoDataVenc && (
                <div style={{ fontSize: 11, color: '#0369a1', marginTop: 4 }}>
                  Serão gerados {boletoParcelas} boletos, 1º vencendo em {new Date(boletoDataVenc + 'T12:00:00').toLocaleDateString('pt-BR')} e demais a cada 30 dias.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowBoleto(false)} style={{ flex: 1, padding: '12px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={gerarBoleto} disabled={gerandoBoleto || !boletoDataVenc}
                style={{ flex: 2, padding: '12px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: (gerandoBoleto || !boletoDataVenc) ? 'not-allowed' : 'pointer', opacity: (gerandoBoleto || !boletoDataVenc) ? 0.7 : 1 }}>
                {gerandoBoleto ? '⏳ Gerando...' : boletoCondicao === 'parcelado' ? `🏦 Gerar ${boletoParcelas} Boletos` : '🏦 Gerar Boleto'}
              </button>
            </div>
          </div>
        )}

        {boletoGerado && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#155724' }}>
                {boletoGerado.parcelas.length > 1 ? `${boletoGerado.parcelas.length} cobranças geradas com sucesso!` : 'Cobrança gerada com sucesso!'}
              </div>
            </div>

            {boletoGerado.parcelas.map((p, i) => (
              <div key={p.cobranca_id || i} style={{ marginBottom: 16 }}>
                {boletoGerado.parcelas.length > 1 && (
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.texto, marginBottom: 6 }}>
                    Parcela {i + 1}/{boletoGerado.parcelas.length}
                  </div>
                )}
                <div style={{ fontSize: 13, color: C.textoSub, marginBottom: 8 }}>
                  Valor: <b>{fmt(p.valor)}</b> · Vence em: <b>{new Date(p.vencimento + 'T12:00:00').toLocaleDateString('pt-BR')}</b>
                </div>

                {/* Boleto */}
                {p.boleto_url && (
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0369a1', marginBottom: 8 }}>📄 Boleto Bancário</div>
                    {p.linha_digitavel && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: C.textoSub, marginBottom: 4 }}>Linha digitável</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input readOnly value={p.linha_digitavel} style={{ ...inputStyle, fontSize: 11, flex: 1, background: 'white' }} />
                          <button onClick={() => copiar(p.linha_digitavel, 'Linha digitável')} style={{ padding: '6px 12px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                            Copiar
                          </button>
                        </div>
                      </div>
                    )}
                    <a href={p.boleto_url} target="_blank" rel="noreferrer"
                      style={{ display: 'block', textAlign: 'center', padding: '10px', background: '#0369a1', color: 'white', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                      Abrir Boleto PDF ↗
                    </a>
                  </div>
                )}

                {/* PIX */}
                {(p.pix_copia_cola || p.pix_qrcode) && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#15803d', marginBottom: 8 }}>⚡ PIX</div>
                    {p.pix_qrcode && (
                      <div style={{ textAlign: 'center', marginBottom: 10 }}>
                        <img src={`data:image/png;base64,${p.pix_qrcode}`} alt="QR Code PIX" style={{ width: 160, height: 160, borderRadius: 8 }} />
                      </div>
                    )}
                    {p.pix_copia_cola && (
                      <div>
                        <div style={{ fontSize: 11, color: C.textoSub, marginBottom: 4 }}>Copia e cola</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input readOnly value={p.pix_copia_cola} style={{ ...inputStyle, fontSize: 11, flex: 1, background: 'white' }} />
                          <button onClick={() => copiar(p.pix_copia_cola, 'PIX Copia e Cola')} style={{ padding: '6px 12px', background: '#15803d', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                            Copiar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={reenviarCobranca} disabled={reenviandoCobranca}
                style={{ flex: 2, padding: '12px', background: '#15803d', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: reenviandoCobranca ? 'not-allowed' : 'pointer', opacity: reenviandoCobranca ? 0.7 : 1 }}>
                {reenviandoCobranca ? '⏳ Enviando...' : '✉️ Enviar Cobrança por Email'}
              </button>
              <button onClick={() => { setShowBoleto(false); setBoletoGerado(null); }}
                style={{ flex: 1, padding: '12px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                Fechar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: Nota Fiscal */}
      <Modal show={!!modalNota} onClose={() => setModalNota(null)} title={`🧾 Nova Venda — Orçamento #${modalNota?.numero}`} maxWidth={760}>
        {modalNota && (() => {
          const abas = ['vendedor', 'produtos', 'cobranca', 'pagamento', 'observacao', 'anexos'];
          const abaLabel = { vendedor: 'Vendedor / Comprador', produtos: 'Produtos / Serviços', cobranca: 'Cobrança', pagamento: 'Pagamento', observacao: 'Observação', anexos: 'Anexos' };
          const lb = { fontSize: 11, fontWeight: 700, color: C.textoSub, display: 'block', marginBottom: 4, textTransform: 'uppercase' };
          const inp = { ...inputStyle, padding: '7px 10px', fontSize: 13 };
          const sel = { ...inp, background: 'white', cursor: 'pointer' };
          const totSvs = totalServicosLiquido(modalNota.servicos || []);
          const totPcs = totalPecasLiquido(modalNota.pecas || []);
          const totalGeral = totSvs + totPcs;

          return (
            <div>
              {/* Cabeçalho: valor total + tipo */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: `1px solid ${C.borda}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textoSub }}>
                  🧾 Emissão automática por tipo de item
                </div>
                <div style={{ fontWeight: 800, fontSize: 18, color: C.texto }}>R$ {totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
              </div>

              {/* Abas */}
              <div style={{ display: 'flex', borderBottom: `2px solid ${C.borda}`, marginBottom: 20, gap: 0, overflowX: 'auto' }}>
                {abas.map(a => (
                  <button key={a} onClick={() => setNfAba(a)} style={{ padding: '8px 14px', border: 'none', borderBottom: `2px solid ${nfAba === a ? C.laranja : 'transparent'}`, marginBottom: -2, background: 'transparent', cursor: 'pointer', fontWeight: nfAba === a ? 700 : 500, fontSize: 12, color: nfAba === a ? C.laranja : C.textoSub, whiteSpace: 'nowrap' }}>
                    {abaLabel[a]}
                  </button>
                ))}
              </div>

              {/* ABA: Vendedor / Comprador */}
              {nfAba === 'vendedor' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                    <div>
                      <label style={lb}>Vendedor (usuário do sistema)</label>
                      <select value={nfVendedor} onChange={e => setNfVendedor(e.target.value)} style={sel}>
                        <option value="">— Selecionar —</option>
                        {nfUsuarios.map(u => <option key={u.id} value={u.nome}>{u.nome}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lb}>Comprador (cliente)</label>
                      <input value={nfComprador} onChange={e => setNfComprador(e.target.value)} placeholder={modalNota.cliente_nome} style={inp} />
                    </div>
                  </div>
                  <div style={{ padding: '12px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, fontSize: 12, color: '#0369a1' }}>
                    <strong>Cliente do orçamento:</strong> {modalNota.cliente_nome}<br />
                    {modalNota.cliente_cnpj && <span>CNPJ: {modalNota.cliente_cnpj}</span>}
                    {modalNota.cliente_ie && <span style={{ marginLeft: 8, color: '#065f46', fontSize: 12 }}>IE: {modalNota.cliente_ie}</span>}
                    {modalNota.cliente_cnpj && !modalNota.cliente_ie && <span style={{ marginLeft: 8, color: '#92400e', fontSize: 12 }}>(sem IE — será enviado como isento)</span>}
                    {modalNota.cliente_cpf  && <span>CPF: {modalNota.cliente_cpf}</span>}
                  </div>
                </div>
              )}

              {/* ABA: Produtos / Serviços */}
              {nfAba === 'produtos' && (
                <div>
                  {/* Tipo de nota — 100% automático */}
                  {(() => {
                    const _svs = itensFiscais.filter(x => x.tipo === 'servico');
                    const _prd = itensFiscais.filter(x => x.tipo === 'produto');
                    const _ambos = _svs.length > 0 && _prd.length > 0;
                    const _label = _ambos
                      ? '🔧 NFS-e (serviços) + 📦 NF-e (produtos)'
                      : _svs.length > 0 ? '🔧 NFS-e (Serviço)' : '📦 NF-e (Produto)';
                    return (
                      <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                        ✅ Nota{_ambos ? 's' : ''} a emitir: {_label}
                      </div>
                    );
                  })()}

                  {/* Resumo de tipos */}
                  {itensFiscais.length > 0 && (() => {
                    const svs2 = itensFiscais.filter(x => x.tipo === 'servico');
                    const prd2 = itensFiscais.filter(x => x.tipo === 'produto');
                    return (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                        {svs2.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, background: '#f0f9ff', color: '#0369a1', padding: '3px 10px', borderRadius: 20 }}>🔧 {svs2.length} serviço{svs2.length > 1 ? 's' : ''}</span>}
                        {prd2.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, background: '#fff8f0', color: C.laranja, padding: '3px 10px', borderRadius: 20 }}>📦 {prd2.length} produto{prd2.length > 1 ? 's' : ''}</span>}
                      </div>
                    );
                  })()}

                  {itensFiscais.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: C.textoSub, fontSize: 13 }}>Nenhum item no orçamento.</div>}

                  {itensFiscais.map((item, i) => (
                    <div key={i} style={{ border: `1px solid ${item.tipo === 'servico' ? '#bae6fd' : C.laranja + '50'}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, background: item.tipo === 'servico' ? '#f0f9ff' : '#fff8f0', color: item.tipo === 'servico' ? '#0369a1' : C.laranja, padding: '2px 8px', borderRadius: 20 }}>
                          {item.tipo === 'servico' ? '🔧 Serviço' : '📦 Produto'}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{item.descricao}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12, marginBottom: 8 }}>
                        <div><div style={{ color: C.textoSub, marginBottom: 2 }}>Qtd</div><div style={{ fontWeight: 700 }}>{item.quantidade}</div></div>
                        <div><div style={{ color: C.textoSub, marginBottom: 2 }}>Valor Unit.</div><div style={{ fontWeight: 700 }}>{fmt(item.valor_unitario)}</div></div>
                        <div><div style={{ color: C.textoSub, marginBottom: 2 }}>Total</div><div style={{ fontWeight: 700, color: C.laranja }}>{fmt(item.valor_total)}</div></div>
                      </div>
                      {item.tipo === 'servico' ? (
                        /* Serviço: código LC 116 */
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 11, color: C.textoSub, marginBottom: 2 }}>Código do Serviço LC 116 {item.codigo_servico ? '✅' : <span style={{ color: '#ef4444' }}>*</span>}</div>
                            <input value={item.codigo_servico || ''} onChange={e => setItemFiscal(i, 'codigo_servico', e.target.value)}
                              placeholder="Ex: 02.19" style={{ ...inp, borderColor: item.codigo_servico ? C.borda : '#fca5a5' }} />
                            {codigoServicoPadrao && item.codigo_servico && item.codigo_servico.trim() !== codigoServicoPadrao && (
                              <button type="button" onClick={() => setItemFiscal(i, 'codigo_servico', codigoServicoPadrao)}
                                style={{ marginTop: 4, background: 'none', border: 'none', padding: 0, fontSize: 11, color: C.laranja, cursor: 'pointer' }}>
                                ↺ Usar padrão ({codigoServicoPadrao})
                              </button>
                            )}
                            {!codigoServicoPadrao && !item.codigo_servico && (
                              <div style={{ marginTop: 4, fontSize: 11, color: C.textoSub }}>Dica: cadastre um código padrão em Configurações &gt; NFe — Emissão Fiscal.</div>
                            )}
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: C.textoSub, marginBottom: 2 }}>Unidade</div>
                            <input value={item.unidade || 'UN'} onChange={e => setItemFiscal(i, 'unidade', e.target.value)} style={inp} />
                          </div>
                        </div>
                      ) : (
                        /* Produto: NCM + CFOP + Unidade */
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 11, color: C.textoSub, marginBottom: 2 }}>NCM {item.ncm ? '✅' : <span style={{ color: '#ef4444' }}>*</span>}</div>
                            <input value={item.ncm || ''} onChange={e => setItemFiscal(i, 'ncm', e.target.value)}
                              placeholder="Ex: 84713012" style={{ ...inp, borderColor: item.ncm ? C.borda : '#fca5a5' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: C.textoSub, marginBottom: 2 }}>CFOP *</div>
                            <input value={item.cfop || '5102'} onChange={e => setItemFiscal(i, 'cfop', e.target.value)} placeholder="5102" style={inp} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: C.textoSub, marginBottom: 2 }}>Unidade</div>
                            <input value={item.unidade || 'UN'} onChange={e => setItemFiscal(i, 'unidade', e.target.value)} style={inp} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* ABA: Cobrança */}
              {nfAba === 'cobranca' && (
                <div>
                  {notasDoOrc.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.textoSub, textTransform: 'uppercase', marginBottom: 8 }}>Notas já emitidas</div>
                      {notasDoOrc.map(n => (
                        <div key={n.id} style={{ border: `1px solid ${C.borda}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontWeight: 700, marginRight: 8, color: n.status === 'autorizada' ? '#16a34a' : n.status === 'rejeitada' ? C.vermelho : C.textoSub }}>
                              {n.status === 'autorizada' ? '✅' : n.status === 'rejeitada' ? '❌' : '⏳'} {n.tipo?.toUpperCase()}
                            </span>
                            {n.numero_nota && <span style={{ fontSize: 12, color: C.textoSub }}>Nº {n.numero_nota}</span>}
                            {n.mensagem_erro && <div style={{ fontSize: 11, color: C.vermelho, marginTop: 3 }}>{n.mensagem_erro}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {n.status === 'processando' && <button onClick={() => consultarNota(n.id)} style={{ fontSize: 11, padding: '4px 10px', background: '#f0f3f7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>🔄 Verificar</button>}
                            {n.url_pdf && <a href={n.url_pdf} target="_blank" rel="noreferrer" style={{ fontSize: 11, padding: '4px 10px', background: C.laranja, color: 'white', borderRadius: 6, textDecoration: 'none', fontWeight: 700 }}>📄 PDF</a>}
                            {n.url_xml && <a href={n.url_xml} target="_blank" rel="noreferrer" style={{ fontSize: 11, padding: '4px 10px', background: '#1A2233', color: 'white', borderRadius: 6, textDecoration: 'none', fontWeight: 700 }}>📋 XML</a>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 8, border: `1px solid ${C.borda}`, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Resumo de cobrança</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div><span style={{ color: C.textoSub }}>Serviços:</span> <strong>{fmt(totSvs)}</strong></div>
                      <div><span style={{ color: C.textoSub }}>Produtos:</span> <strong>{fmt(totPcs)}</strong></div>
                      <div style={{ gridColumn: '1/-1', borderTop: `1px solid ${C.borda}`, paddingTop: 8, fontWeight: 700, fontSize: 15 }}>Total: <span style={{ color: C.laranja }}>{fmt(totalGeral)}</span></div>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA: Pagamento */}
              {nfAba === 'pagamento' && (
                <div>
                  {/* Linha 1: Forma de Pagamento + Condição */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div>
                      <label style={lb}>Forma de Pagamento</label>
                      <select value={nfFormaPagamento} onChange={e => setNfFormaPagamento(e.target.value)} style={sel}>
                        <option value="boleto">Boleto</option>
                      </select>
                    </div>
                    <div>
                      <label style={lb}>Condição</label>
                      <select value={nfCondicao} onChange={e => {
                        setNfCondicao(e.target.value);
                        if (e.target.value !== 'parcelado') setNfParcelas('2');
                      }} style={sel}>
                        <option value="a_vista">À Vista</option>
                        <option value="a_prazo">A Prazo</option>
                        <option value="parcelado">Parcelado</option>
                      </select>
                    </div>
                  </div>

                  {/* Linha 2: Parcelas (só aparece se Parcelado) + Data de Vencimento */}
                  <div style={{ display: 'grid', gridTemplateColumns: nfCondicao === 'parcelado' ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 14 }}>
                    {nfCondicao === 'parcelado' && (
                      <div>
                        <label style={lb}>Número de Parcelas</label>
                        <select value={nfParcelas} onChange={e => setNfParcelas(e.target.value)} style={sel}>
                          {Array.from({ length: 11 }, (_, i) => i + 2).map(n => (
                            <option key={n} value={String(n)}>{n}x</option>
                          ))}
                        </select>
                        {nfGerarBoleto && (
                          <div style={{ fontSize: 11, color: '#0369a1', marginTop: 4 }}>
                            Serão gerados {nfParcelas} boletos, 1º vencimento na data escolhida e demais a cada 30 dias.
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <label style={lb}>
                        Data de Vencimento{nfGerarBoleto ? ' *' : ''}
                      </label>
                      <input
                        type="date"
                        value={nfDataVenc}
                        onChange={e => setNfDataVenc(e.target.value)}
                        style={{ ...inp, borderColor: nfGerarBoleto && !nfDataVenc ? '#ef4444' : undefined }}
                      />
                      {nfGerarBoleto && !nfDataVenc && (
                        <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Obrigatória para gerar cobrança ASAAS</div>
                      )}
                    </div>
                  </div>

                  {/* Toggle ASAAS */}
                  <div style={{ marginTop: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <div onClick={() => setNfGerarBoleto(p => !p)} style={{ width: 40, height: 22, borderRadius: 11, background: nfGerarBoleto ? C.laranja : '#d4dde8', position: 'relative', transition: 'background .2s', flexShrink: 0, cursor: 'pointer' }}>
                        <div style={{ position: 'absolute', top: 3, left: nfGerarBoleto ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.texto }}>Gerar cobrança ASAAS após emitir</div>
                        <div style={{ fontSize: 11, color: C.textoSub }}>
                          {nfCondicao === 'parcelado' && nfGerarBoleto
                            ? `Cria ${nfParcelas} boletos automaticamente ao emitir a nota`
                            : 'Cria boleto automaticamente ao emitir a nota'}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* ABA: Observação */}
              {nfAba === 'observacao' && (
                <div>
                  <label style={lb}>Observações da nota fiscal</label>
                  <textarea value={nfObs} onChange={e => setNfObs(e.target.value)} rows={6} placeholder="Informações adicionais para a nota fiscal..." style={{ ...inp, width: '100%', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
              )}

              {/* ABA: Anexos */}
              {nfAba === 'anexos' && (
                <div>
                  <div style={{ border: `2px dashed ${C.borda}`, borderRadius: 8, padding: '32px', textAlign: 'center', marginBottom: 14, cursor: 'pointer' }}
                    onClick={() => document.getElementById('nf-upload').click()}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
                    <div style={{ fontSize: 13, color: C.textoSub }}>Clique ou arraste arquivos aqui</div>
                    <input id="nf-upload" type="file" multiple style={{ display: 'none' }}
                      onChange={e => setNfAnexos(prev => [...prev, ...Array.from(e.target.files)])} />
                  </div>
                  {nfAnexos.length > 0 && (
                    <div>
                      {nfAnexos.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 6, marginBottom: 6, fontSize: 13 }}>
                          <span>📄 {f.name}</span>
                          <button onClick={() => setNfAnexos(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: C.vermelho, cursor: 'pointer', fontSize: 16 }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rodapé fixo com botões */}
              <div style={{ display: 'flex', gap: 8, marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.borda}`, flexWrap: 'wrap' }}>
                <button onClick={() => setModalNota(null)} style={{ padding: '10px 20px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  Cancelar
                </button>
                <div style={{ flex: 1 }} />
                <button onClick={salvarAlteracoesItens} disabled={salvandoItens || itensFiscais.length === 0}
                  title="Salva as correções (NCM, CFOP, código de serviço) no orçamento sem emitir a nota"
                  style={{ padding: '10px 20px', background: '#fff', color: C.azul, border: `1px solid ${C.azul}`, borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: salvandoItens ? 'not-allowed' : 'pointer', opacity: itensFiscais.length === 0 ? 0.5 : 1 }}>
                  {salvandoItens ? '⏳ Salvando...' : '💾 Salvar Alterações'}
                </button>
                <button onClick={() => emitirNota(false)} disabled={emitindoNota || itensFiscais.length === 0}
                  style={{ padding: '10px 24px', background: '#1A2233', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: emitindoNota ? 'not-allowed' : 'pointer', opacity: itensFiscais.length === 0 ? 0.5 : 1 }}>
                  {emitindoNota ? '⏳ Emitindo...' : '🧾 Salvar e Emitir'}
                </button>
                <button onClick={() => emitirNota(true)} disabled={emitindoNota || nfGerandoBoleto || itensFiscais.length === 0}
                  style={{ padding: '10px 24px', background: C.laranja, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: (emitindoNota || nfGerandoBoleto) ? 'not-allowed' : 'pointer', opacity: itensFiscais.length === 0 ? 0.5 : 1 }}>
                  {emitindoNota || nfGerandoBoleto ? '⏳ Processando...' : '🧾💳 Salvar e Enviar'}
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Modal: Enviar por Email */}
      <Modal show={showEmail} onClose={() => setShowEmail(false)} title="✉️ Enviar Orçamento por Email" maxWidth={560} bloqueado={enviandoEmail}>
        {emailOrc && (
          <div>
            {/* Resumo */}
            <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: C.textoSub, marginBottom: 2 }}>Orçamento Nº {emailOrc.numero}</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: C.texto }}>{emailOrc.cliente_nome}</div>
            </div>

            {/* Destinatários */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Destinatários</label>
              {emailDestinatarios.length === 0 ? (
                <div style={{ fontSize: 13, color: C.vermelho, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                  Nenhum email cadastrado para este cliente.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {emailDestinatarios.map((d, i) => (
                    <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: d.selecionado ? '#f0fdf4' : '#f7f8fa', border: `1px solid ${d.selecionado ? '#bbf7d0' : C.borda}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={d.selecionado}
                        onChange={() => setEmailDestinatarios(prev => prev.map((x, j) => j === i ? { ...x, selecionado: !x.selecionado } : x))}
                        style={{ width: 16, height: 16, accentColor: C.verde, flexShrink: 0 }}
                      />
                      <span style={{ color: C.texto, flex: 1 }}>{d.email}</span>
                      {i === 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1e40af', borderRadius: 4, padding: '1px 6px' }}>PRINCIPAL</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Assunto */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Assunto</label>
              <input
                value={emailAssunto}
                onChange={e => setEmailAssunto(e.target.value)}
                placeholder="Assunto do email"
                style={inputStyle}
              />
            </div>

            {/* Corpo */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Mensagem</label>
              <textarea
                value={emailCorpo}
                onChange={e => setEmailCorpo(e.target.value)}
                rows={8}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              />
              <div style={{ fontSize: 11, color: C.textoSub, marginTop: 4 }}>
                O PDF do orçamento será enviado como anexo automaticamente.
              </div>
            </div>

            {/* Botões */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowEmail(false)} disabled={enviandoEmail}
                style={{ flex: 1, padding: '12px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={enviarEmail} disabled={enviandoEmail || emailDestinatarios.every(d => !d.selecionado)}
                style={{ flex: 2, padding: '12px', background: '#15803d', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: enviandoEmail || emailDestinatarios.every(d => !d.selecionado) ? 'not-allowed' : 'pointer', opacity: emailDestinatarios.every(d => !d.selecionado) ? 0.6 : 1 }}>
                {enviandoEmail ? '⏳ Enviando...' : '✉️ Enviar Email'}
              </button>
            </div>

            {/* Histórico de envios */}
            <div style={{ marginTop: 24, borderTop: `1px solid ${C.borda}`, paddingTop: 18 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.textoSub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                📬 Histórico de Envios
              </div>
              {carregandoLog ? (
                <div style={{ color: C.textoSub, fontSize: 13 }}>Carregando...</div>
              ) : logEnvios.length === 0 ? (
                <div style={{ color: C.textoSub, fontSize: 13, background: '#f7f8fa', borderRadius: 8, padding: '12px 14px' }}>
                  Nenhum email enviado ainda para este orçamento.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {logEnvios.map(log => (
                    <div key={log.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: log.status === 'enviado' ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${log.status === 'enviado' ? '#bbf7d0' : '#fecaca'}`,
                      borderRadius: 8, padding: '8px 12px', fontSize: 13,
                    }}>
                      <span style={{
                        background: log.status === 'enviado' ? '#dcfce7' : '#fee2e2',
                        color: log.status === 'enviado' ? '#16a34a' : '#dc2626',
                        borderRadius: 4, padding: '2px 8px', fontWeight: 700, fontSize: 11, flexShrink: 0,
                      }}>
                        {log.status === 'enviado' ? '✓ Enviado' : '✗ Erro'}
                      </span>
                      <span style={{ color: C.textoSub, flexShrink: 0 }}>{log.enviado_em_fmt}</span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.texto }}>
                        {log.destinatario}
                      </span>
                      {log.erro_detalhe && (
                        <span
                          title={log.erro_detalhe}
                          style={{ color: '#dc2626', fontSize: 11, flexShrink: 0, cursor: 'help', textDecoration: 'underline dotted' }}>
                          ver erro
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: Resumo Financeiro */}
      <Modal show={showResumo} onClose={() => setShowResumo(false)} title="📊 Resumo Financeiro" maxWidth={540}>
        {(() => {
          const meses = [
            { v: '', l: 'Todos os meses' },
            { v: '01', l: 'Janeiro' }, { v: '02', l: 'Fevereiro' }, { v: '03', l: 'Março' },
            { v: '04', l: 'Abril' },   { v: '05', l: 'Maio' },      { v: '06', l: 'Junho' },
            { v: '07', l: 'Julho' },   { v: '08', l: 'Agosto' },    { v: '09', l: 'Setembro' },
            { v: '10', l: 'Outubro' }, { v: '11', l: 'Novembro' },  { v: '12', l: 'Dezembro' },
          ];
          const anosDisponiveis = [...new Set(orcamentos.map(o => new Date(o.criado_em).getFullYear()))].sort((a,b) => b - a);
          if (!anosDisponiveis.includes(resumoAno) && anosDisponiveis.length > 0) {
            // garante que o ano default seja válido
          }

          // Filtra orçamentos pelo mês/ano selecionado
          const orcFiltradosMes = orcamentos.filter(o => {
            const d = new Date(o.criado_em);
            const anoOk = d.getFullYear() === resumoAno;
            const mesOk = !resumoMes || String(d.getMonth() + 1).padStart(2, '0') === resumoMes;
            return anoOk && mesOk;
          });

          const calcTotal = (lista) => lista.reduce((s, o) =>
            s + totalServicosLiquido(o.servicos || []) + totalPecasLiquido(o.pecas || []), 0);

          const totalGeral = calcTotal(orcFiltradosMes);
          const itens = Object.entries(STATUS).map(([k, v]) => {
            const lista = orcFiltradosMes.filter(o => o.status === k);
            return { key: k, label: v.label, color: v.color, bg: v.bg, count: lista.length, total: calcTotal(lista) };
          });

          const labelPeriodo = resumoMes
            ? `${meses.find(m => m.v === resumoMes)?.l} / ${resumoAno}`
            : `Ano ${resumoAno}`;

          return (
            <div>
              {/* Filtros */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <select value={resumoMes} onChange={e => setResumoMes(e.target.value)}
                  style={{ flex: 2, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.borda}`, fontSize: 13, background: 'white', cursor: 'pointer' }}>
                  {meses.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
                <select value={resumoAno} onChange={e => setResumoAno(Number(e.target.value))}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.borda}`, fontSize: 13, background: 'white', cursor: 'pointer' }}>
                  {(anosDisponiveis.length > 0 ? anosDisponiveis : [new Date().getFullYear()]).map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>

              {/* Total geral do período */}
              <div style={{ background: '#fff8f0', border: `1px solid ${C.laranja}`, borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textoSub, textTransform: 'uppercase' }}>Total — {labelPeriodo}</div>
                  <div style={{ fontSize: 11, color: C.textoSub, marginTop: 2 }}>{orcFiltradosMes.length} orçamento(s)</div>
                </div>
                <div style={{ fontSize: 26, fontWeight: 900, color: C.laranja }}>{fmt(totalGeral)}</div>
              </div>

              {/* Por status */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {itens.map(r => (
                  <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', background: '#f7f8fa', borderRadius: 10, border: `1px solid ${C.borda}` }}>
                    <span style={{ background: r.bg, color: r.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', minWidth: 110, textAlign: 'center' }}>{r.label}</span>
                    <div style={{ fontSize: 12, color: C.textoSub, flex: 1 }}>{r.count} orçamento(s)</div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: r.total > 0 ? C.texto : C.textoSub }}>{fmt(r.total)}</div>
                    {totalGeral > 0 && (
                      <div style={{ fontSize: 11, color: C.textoSub, minWidth: 34, textAlign: 'right' }}>
                        {((r.total / totalGeral) * 100).toFixed(0)}%
                      </div>
                    )}
                  </div>
                ))}
                {orcFiltradosMes.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 24, color: C.textoSub, fontSize: 13 }}>
                    Nenhum orçamento encontrado neste período.
                  </div>
                )}
              </div>

              <button onClick={() => setShowResumo(false)}
                style={{ width: '100%', marginTop: 18, padding: '12px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                Fechar
              </button>
            </div>
          );
        })()}
      </Modal>

    </div>
  );
}
